import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Users, UserCheck, UserPlus, Clock, Mail, Building2, Building, Plus, Upload, ArrowLeft, Calendar, Pencil, Trash2, Search, CheckCircle, X, FileText, Settings2, Shield, Tag, Download, Share2, Copy } from 'lucide-react';
import axios from 'axios';
import ApplicationDetailsDialog from '@/components/ApplicationDetailsDialog';
import FormBuilder from '@/components/FormBuilder';
import DynamicFormRenderer from '@/components/DynamicFormRenderer';
import DynamicDealForm from '@/components/DynamicDealForm';
import DynamicEntityForm from '@/components/DynamicEntityForm';
import AccessTagsSelector, { AccessTagsBadges } from '@/components/AccessTagsSelector';
import branding from '@/config/branding';
import { AddInvestorToDeal, AddDealToInvestor, EditCommitmentDialog } from '@/components/InvestmentManager';
import DealCustomFields from '@/components/DealCustomFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to construct correct deck URL regardless of stored format
const getDeckUrl = (deckUrl) => {
  if (!deckUrl) return null;
  
  // If it's already an S3 URL or other absolute URL, use it directly
  if (deckUrl.startsWith('https://') && (deckUrl.includes('.s3.') || deckUrl.includes('amazonaws.com'))) {
    return deckUrl;
  }
  
  let deckPath = deckUrl;
  // If it's a full URL from old deployment, extract just the path
  if (deckPath.startsWith('http')) {
    try {
      const url = new URL(deckPath);
      deckPath = url.pathname;
    } catch (e) {
      // Fallback: extract path after domain
      deckPath = '/' + deckPath.split('/').slice(3).join('/');
    }
  }
  // Ensure path uses /api/uploads for ingress routing
  if (deckPath.startsWith('/uploads')) {
    deckPath = deckPath.replace('/uploads', '/api/uploads');
  } else if (!deckPath.startsWith('/api/uploads')) {
    deckPath = '/api/uploads/' + deckPath.split('/').pop();
  }
  return `${process.env.REACT_APP_BACKEND_URL}${deckPath}`;
};

// Validation helpers
const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

const isValidPhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/[^\d+]/g, '');
  const phoneRegex = /^\+?\d{10,15}$/;
  return phoneRegex.test(cleaned);
};

const formatPhoneInput = (value) => {
  let cleaned = value.replace(/[^\d+]/g, '');
  if (cleaned.indexOf('+') > 0) {
    cleaned = cleaned.replace(/\+/g, '');
  }
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
  }
  return cleaned;
};

const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

const isValidLinkedIn = (url) => {
  if (!url || !isValidUrl(url)) return false;
  const urlLower = url.toLowerCase();
  return urlLower.includes('linkedin.com/in/') || urlLower.includes('linkedin.com/company/');
};

const isPositiveNumber = (num) => {
  if (num === '' || num === null || num === undefined) return true;
  return !isNaN(num) && Number(num) >= 0;
};

const formatNumberInput = (value) => {
  return value.replace(/[^\d]/g, '');
};

function formatINR(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
}

// Consistent color scheme for entity types across the platform
// Each entity type has ONE color used for both subtabs and tags
const ENTITY_COLORS = {
  events: {
    tab: 'bg-emerald-500',
    tabText: 'text-white',
    tagBg: 'bg-emerald-50',
    tagText: 'text-emerald-700',
    tagBorder: 'border-emerald-200'
  },
  investment_bankers: {
    tab: 'bg-purple-500',
    tabText: 'text-white',
    tagBg: 'bg-purple-50',
    tagText: 'text-purple-700',
    tagBorder: 'border-purple-200'
  },
  funds: {
    tab: 'bg-amber-500',
    tabText: 'text-white',
    tagBg: 'bg-amber-50',
    tagText: 'text-amber-700',
    tagBorder: 'border-amber-200'
  },
  angel_networks: {
    tab: 'bg-pink-500',
    tabText: 'text-white',
    tagBg: 'bg-pink-50',
    tagText: 'text-pink-700',
    tagBorder: 'border-pink-200'
  }
};

// Helper to get tag classes for an entity type
function getTagClasses(entityType) {
  const colors = ENTITY_COLORS[entityType];
  return `${colors.tagBg} ${colors.tagText} border ${colors.tagBorder}`;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { tab: urlTab, subtab: urlSubtab } = useParams();
  const [pendingMembers, setPendingMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [mockEmails, setMockEmails] = useState([]); // Keep for potential future use
  const [loading, setLoading] = useState(true);
  
  // Use URL params for tabs, with fallback to defaults
  const activeTab = urlTab || 'deals';
  const usersSubTab = (urlTab === 'users' && urlSubtab) ? urlSubtab : 'active';
  const dealsSubTab = (urlTab === 'deals' && urlSubtab) ? urlSubtab : 'deals';
  
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  
  // Tab change handlers that update URL
  const setActiveTab = (tab) => {
    navigate(`/admin/${tab}`);
  };
  
  const setUsersSubTab = (subtab) => {
    navigate(`/admin/users/${subtab}`);
  };
  
  const setDealsSubTab = (subtab) => {
    navigate(`/admin/deals/${subtab}`);
  };
  
  // Startup Applications state
  const [startupApplications, setStartupApplications] = useState([]);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApplicationDetailsDialog, setShowApplicationDetailsDialog] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [approvalData, setApprovalData] = useState({
    event_category: '',
    investment_bankers: [],
    funds: [],
    angel_networks: []
  });
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Add New Entity in Approval Dialog
  const [showAddEventInApproval, setShowAddEventInApproval] = useState(false);
  const [showAddBankerInApproval, setShowAddBankerInApproval] = useState(false);
  const [showAddFundInApproval, setShowAddFundInApproval] = useState(false);
  const [showAddNetworkInApproval, setShowAddNetworkInApproval] = useState(false);
  const [newEntityName, setNewEntityName] = useState('');
  
  // Get past 5 months for revenue
  const getPast5Months = () => {
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    for (let i = 5; i >= 1; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${monthNames[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`);
    }
    return months;
  };
  const revenueMonths = getPast5Months();
  
  // Add Deal Dialog state - Matches startup form with entity dropdowns
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const [dealFormErrors, setDealFormErrors] = useState({});
  const [newDeal, setNewDeal] = useState({
    // Startup info fields
    legal_name: '',
    brand_name: '',
    website: '',
    location: '',
    founders: [{ name: '', email: '', phone: '', linkedin: '' }],
    stage: '',
    category: '',
    one_liner: '',
    description: '',
    product: '',
    product_pricing: '',
    revenue: {
      month1: 0, month2: 0, month3: 0, month4: 0, month5: 0,
      month1_label: revenueMonths[0], month2_label: revenueMonths[1], 
      month3_label: revenueMonths[2], month4_label: revenueMonths[3], month5_label: revenueMonths[4]
    },
    previous_fundraises: [], // Array of { type: 'Fundraise'|'Grant', valuation: '', grant_name: '', amount: '' }
    deck_url: '',
    demo_link: '',
    amount_to_raise: '',
    terms_type: '',
    valuation_amount: '',
    safe_terms: '',
    other_terms: '',
    // Entity dropdowns (admin only)
    event_category: '',
    investment_bankers: [],
    funds: [],
    angel_networks: []
  });
  
  // Edit Deal Dialog state
  const [showEditDealDialog, setShowEditDealDialog] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [uploadingEditDeck, setUploadingEditDeck] = useState(false);
  const [editFormErrors, setEditFormErrors] = useState({});
  
  // Invite Dialog state
  const [inviteData, setInviteData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  
  // Deal name validation state
  const [isDuplicateName, setIsDuplicateName] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [nameCheckTimeout, setNameCheckTimeout] = useState(null);
  
  // Commitment viewing state
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealCommitments, setDealCommitments] = useState([]);
  const [showCommitmentsDialog, setShowCommitmentsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCommitments, setUserCommitments] = useState([]);
  const [showUserCommitmentsDialog, setShowUserCommitmentsDialog] = useState(false);
  
  // Investment management state
  const [showAddInvestorDialog, setShowAddInvestorDialog] = useState(false);
  const [showAddDealDialog, setShowAddDealDialog] = useState(false);
  const [editingCommitment, setEditingCommitment] = useState(null);
  const [showEditCommitmentDialog, setShowEditCommitmentDialog] = useState(false);
  const [deletingCommitment, setDeletingCommitment] = useState(null);
  
  // Events management state
  const [events, setEvents] = useState([]);
  const [newEventName, setNewEventName] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [editEventName, setEditEventName] = useState('');
  const [showAddEventInDealForm, setShowAddEventInDealForm] = useState(false);
  const [newEventInDealForm, setNewEventInDealForm] = useState('');

  // Investment Bankers management state
  const [investmentBankers, setInvestmentBankers] = useState([]);
  const [newBankerName, setNewBankerName] = useState('');
  const [editingBanker, setEditingBanker] = useState(null);
  const [editBankerName, setEditBankerName] = useState('');
  const [showAddBankerInDealForm, setShowAddBankerInDealForm] = useState(false);
  const [newBankerInDealForm, setNewBankerInDealForm] = useState('');
  const [showBankerDialog, setShowBankerDialog] = useState(false);
  const [editingBankerEntity, setEditingBankerEntity] = useState(null);

  // Funds management state
  const [funds, setFunds] = useState([]);
  const [newFundName, setNewFundName] = useState('');
  const [editingFund, setEditingFund] = useState(null);
  const [editFundName, setEditFundName] = useState('');
  const [showAddFundInDealForm, setShowAddFundInDealForm] = useState(false);
  const [newFundInDealForm, setNewFundInDealForm] = useState('');
  const [showFundDialog, setShowFundDialog] = useState(false);
  const [editingFundEntity, setEditingFundEntity] = useState(null);

  const [angelNetworks, setAngelNetworks] = useState([]);
  const [newNetworkName, setNewNetworkName] = useState('');
  const [editingNetwork, setEditingNetwork] = useState(null);
  const [editNetworkName, setEditNetworkName] = useState('');
  const [showNetworkDialog, setShowNetworkDialog] = useState(false);
  const [editingNetworkEntity, setEditingNetworkEntity] = useState(null);  const [showAddNetworkInDealForm, setShowAddNetworkInDealForm] = useState(false);
  const [newNetworkInDealForm, setNewNetworkInDealForm] = useState('');

  // Search state for all tabs
  const [searchDeals, setSearchDeals] = useState('');
  const [searchEvents, setSearchEvents] = useState('');
  const [searchBankers, setSearchBankers] = useState('');
  const [searchFunds, setSearchFunds] = useState('');
  const [searchNetworks, setSearchNetworks] = useState('');
  const [searchUsers, setSearchUsers] = useState('');
  const [filterUserRole, setFilterUserRole] = useState('all');

  // Deal filter state
  const [filterDealType, setFilterDealType] = useState('all');
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterBanker, setFilterBanker] = useState('all');
  const [filterFund, setFilterFund] = useState('all');
  const [filterNetwork, setFilterNetwork] = useState('all');

  // User associations state
  const [editingUserAssociations, setEditingUserAssociations] = useState(null);
  const [showUserAssociationsDialog, setShowUserAssociationsDialog] = useState(false);
  const [userAssociationsData, setUserAssociationsData] = useState({
    investment_bankers: [],
    funds: [],
    angel_networks: []
  });

  // Edit Profile state
  const [showEditProfileDialog, setShowEditProfileDialog] = useState(false);
  const [editingProfileUser, setEditingProfileUser] = useState(null);
  const [editProfileData, setEditProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    capital_deployment: ''
  });

  // Access Tags state
  const [showAccessTagsDialog, setShowAccessTagsDialog] = useState(false);
  const [accessTagsUser, setAccessTagsUser] = useState(null);
  const [accessTagsMode, setAccessTagsMode] = useState('approve'); // 'approve' or 'edit'

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    const userEmail = localStorage.getItem('user_email');
    if (!userEmail) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/profile?email=${userEmail}`);
      setCurrentUser(response.data);
      setUserRole(response.data.role);
      
      // Check if user has admin or team role
      if (!response.data.can_manage_members) {
        toast.error('Access denied. Admin or Team role required.');
        navigate('/dashboard');
        return;
      }
      
      fetchData(userEmail);
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/login');
    }
  };

  const fetchData = async (userEmail) => {
    try {
      const [pending, users, dealsData, emails, eventsData, bankersData, fundsData, networksData, applicationsData] = await Promise.all([
        axios.get(`${API}/admin/pending-members?requester_email=${userEmail}`),
        axios.get(`${API}/admin/users?requester_email=${userEmail}`),
        axios.get(`${API}/deals`),
        axios.get(`${API}/admin/mock-emails?requester_email=${userEmail}`),
        axios.get(`${API}/events`),
        axios.get(`${API}/investment-bankers`),
        axios.get(`${API}/funds`),
        axios.get(`${API}/angel-networks`),
        axios.get(`${API}/admin/startup-applications?requester_email=${userEmail}&status=pending`)
      ]);
      
      setPendingMembers(pending.data);
      setAllUsers(users.data);
      setEvents(eventsData.data);
      setInvestmentBankers(bankersData.data);
      setFunds(fundsData.data);
      setAngelNetworks(networksData.data);
      setStartupApplications(applicationsData.data);
      
      // Fetch total committed capital for each deal
      const dealsWithCommitments = await Promise.all(
        dealsData.data.map(async (deal) => {
          try {
            const commitmentResponse = await axios.get(`${API}/deals/${deal.id}/total-committed`);
            return {
              ...deal,
              total_committed: commitmentResponse.data.total_committed,
              commitment_count: commitmentResponse.data.commitment_count
            };
          } catch (error) {
            return {
              ...deal,
              total_committed: 0,
              commitment_count: 0
            };
          }
        })
      );
      
      setDeals(dealsWithCommitments);
      setMockEmails(emails.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Open access tags dialog for user approval
  const openAccessTagsForApproval = (member) => {
    setAccessTagsUser(member);
    setAccessTagsMode('approve');
    setShowAccessTagsDialog(true);
  };

  // Open access tags dialog for editing existing user
  const openAccessTagsForEdit = (user) => {
    setAccessTagsUser(user);
    setAccessTagsMode('edit');
    setShowAccessTagsDialog(true);
  };

  // Handle saving access tags (for both approve and edit)
  const handleSaveAccessTags = async (accessTags) => {
    const userEmail = localStorage.getItem('user_email');
    try {
      if (accessTagsMode === 'approve') {
        // Approve user with access tags
        await axios.post(
          `${API}/admin/approve/${accessTagsUser.email}?approver_email=${userEmail}`,
          { access_tags: accessTags }
        );
        toast.success('Member approved with access tags!');
      } else {
        // Update existing user's access tags
        await axios.put(
          `${API}/admin/users/${accessTagsUser.email}/access-tags?admin_email=${userEmail}`,
          { access_tags: accessTags }
        );
        toast.success('Access tags updated successfully!');
      }
      fetchData(userEmail);
      setShowAccessTagsDialog(false);
      setAccessTagsUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save access tags');
      throw error;
    }
  };

  const handleApprove = async (email) => {
    // Find the member and open the access tags dialog
    const member = pendingMembers.find(m => m.email === email);
    if (member) {
      openAccessTagsForApproval(member);
    }
  };

  const handleReject = async (email) => {
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/reject/${email}?rejector_email=${userEmail}`);
      toast.success('Member rejected');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject member');
    }
  };

  // Startup Application management functions
  const handleViewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setShowApplicationDetailsDialog(true);
  };

  const handleOpenApproveDialog = (application) => {
    setSelectedApplication(application);
    setApprovalData({
      event_category: '',
      investment_bankers: [],
      funds: [],
      angel_networks: []
    });
    setShowApproveDialog(true);
    setShowApplicationDetailsDialog(false);
  };

  const handleOpenRejectDialog = (application) => {
    setSelectedApplication(application);
    setRejectionReason('');
    setShowRejectDialog(true);
    setShowApplicationDetailsDialog(false);
  };

  // Add new entity handlers for approval dialog
  const handleAddNewEventInApproval = async () => {
    if (!newEntityName.trim()) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/events?admin_email=${userEmail}`, { name: newEntityName.trim() });
      toast.success('Event created!');
      setApprovalData({ ...approvalData, event_category: newEntityName.trim() });
      setShowAddEventInApproval(false);
      setNewEntityName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleAddNewBankerInApproval = async () => {
    if (!newEntityName.trim()) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/investment-bankers?admin_email=${userEmail}`, { name: newEntityName.trim() });
      toast.success('Investment Banker created!');
      setApprovalData({ ...approvalData, investment_bankers: [...(approvalData.investment_bankers || []), newEntityName.trim()] });
      setShowAddBankerInApproval(false);
      setNewEntityName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create banker');
    }
  };

  const handleAddNewFundInApproval = async () => {
    if (!newEntityName.trim()) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/funds?admin_email=${userEmail}`, { name: newEntityName.trim() });
      toast.success('Fund created!');
      setApprovalData({ ...approvalData, funds: [...(approvalData.funds || []), newEntityName.trim()] });
      setShowAddFundInApproval(false);
      setNewEntityName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fund');
    }
  };

  const handleAddNewNetworkInApproval = async () => {
    if (!newEntityName.trim()) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/angel-networks?admin_email=${userEmail}`, { name: newEntityName.trim() });
      toast.success('Angel Network created!');
      setApprovalData({ ...approvalData, angel_networks: [...(approvalData.angel_networks || []), newEntityName.trim()] });
      setShowAddNetworkInApproval(false);
      setNewEntityName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create network');
    }
  };

  const handleApproveApplication = async () => {
    if (!selectedApplication) return;
    
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(
        `${API}/admin/startup-applications/${selectedApplication.id}/approve?requester_email=${encodeURIComponent(userEmail)}`,
        approvalData
      );
      toast.success('Application approved and deal created!');
      setShowApproveDialog(false);
      setSelectedApplication(null);
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    }
  };

  const handleRejectApplication = async () => {
    if (!selectedApplication || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(
        `${API}/admin/startup-applications/${selectedApplication.id}/reject?requester_email=${encodeURIComponent(userEmail)}`,
        { reason: rejectionReason.trim() }
      );
      toast.success('Application rejected');
      setShowRejectDialog(false);
      setSelectedApplication(null);
      setRejectionReason('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    }
  };

  const toggleApprovalBanker = (bankerName) => {
    const current = approvalData.investment_bankers || [];
    if (current.includes(bankerName)) {
      setApprovalData({ ...approvalData, investment_bankers: current.filter(b => b !== bankerName) });
    } else {
      setApprovalData({ ...approvalData, investment_bankers: [...current, bankerName] });
    }
  };

  const toggleApprovalFund = (fundName) => {
    const current = approvalData.funds || [];
    if (current.includes(fundName)) {
      setApprovalData({ ...approvalData, funds: current.filter(f => f !== fundName) });
    } else {
      setApprovalData({ ...approvalData, funds: [...current, fundName] });
    }
  };

  const toggleApprovalNetwork = (networkName) => {
    const current = approvalData.angel_networks || [];
    if (current.includes(networkName)) {
      setApprovalData({ ...approvalData, angel_networks: current.filter(n => n !== networkName) });
    } else {
      setApprovalData({ ...approvalData, angel_networks: [...current, networkName] });
    }
  };

  // Event management functions
  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      toast.error('Please enter event name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/events?admin_email=${userEmail}`, { name: newEventName.trim() });
      toast.success('Event created successfully!');
      setNewEventName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleUpdateEvent = async (eventId) => {
    if (!editEventName.trim()) {
      toast.error('Please enter event name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(`${API}/admin/events/${eventId}?admin_email=${userEmail}`, { name: editEventName.trim() });
      toast.success('Event updated successfully!');
      setEditingEvent(null);
      setEditEventName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.delete(`${API}/admin/events/${eventId}?admin_email=${userEmail}`);
      toast.success('Event deleted successfully!');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleAddEventInDealForm = async () => {
    if (!newEventInDealForm.trim()) {
      toast.error('Please enter event name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.post(`${API}/admin/events?admin_email=${userEmail}`, { name: newEventInDealForm.trim() });
      toast.success('Event created successfully!');
      setEvents([...events, response.data.event]);
      setNewDeal({ ...newDeal, event_category: newEventInDealForm.trim() });
      setNewEventInDealForm('');
      setShowAddEventInDealForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  // Investment Banker management functions
  const handleCreateBanker = async () => {
    if (!newBankerName.trim()) {
      toast.error('Please enter investment banker name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/investment-bankers?admin_email=${userEmail}`, { name: newBankerName.trim() });
      toast.success('Investment Banker created successfully!');
      setNewBankerName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create investment banker');
    }
  };

  const handleUpdateBanker = async (bankerId) => {
    if (!editBankerName.trim()) {
      toast.error('Please enter investment banker name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(`${API}/admin/investment-bankers/${bankerId}?admin_email=${userEmail}`, { name: editBankerName.trim() });
      toast.success('Investment Banker updated successfully!');
      setEditingBanker(null);
      setEditBankerName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update investment banker');
    }
  };

  const handleDeleteBanker = async (bankerId) => {
    if (!window.confirm('Are you sure you want to delete this investment banker?')) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.delete(`${API}/admin/investment-bankers/${bankerId}?admin_email=${userEmail}`);
      toast.success('Investment Banker deleted successfully!');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete investment banker');
    }
  };

  const handleAddBankerInDealForm = async () => {
    if (!newBankerInDealForm.trim()) {
      toast.error('Please enter investment banker name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.post(`${API}/admin/investment-bankers?admin_email=${userEmail}`, { name: newBankerInDealForm.trim() });
      toast.success('Investment Banker created successfully!');
      setInvestmentBankers([...investmentBankers, response.data.investment_banker]);
      // Add the new banker to selected bankers
      const currentBankers = newDeal.investment_bankers || [];
      setNewDeal({ ...newDeal, investment_bankers: [...currentBankers, newBankerInDealForm.trim()] });
      setNewBankerInDealForm('');
      setShowAddBankerInDealForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create investment banker');
    }
  };

  const toggleBankerSelection = (bankerName) => {
    const currentBankers = newDeal.investment_bankers || [];
    if (currentBankers.includes(bankerName)) {
      setNewDeal({ ...newDeal, investment_bankers: currentBankers.filter(b => b !== bankerName) });
    } else {
      setNewDeal({ ...newDeal, investment_bankers: [...currentBankers, bankerName] });
    }
  };

  const toggleEditBankerSelection = (bankerName) => {
    const currentBankers = editingDeal?.investment_bankers || [];
    if (currentBankers.includes(bankerName)) {
      setEditingDeal({ ...editingDeal, investment_bankers: currentBankers.filter(b => b !== bankerName) });
    } else {
      setEditingDeal({ ...editingDeal, investment_bankers: [...currentBankers, bankerName] });
    }
  };

  // Funds management functions
  const handleCreateFund = async () => {
    if (!newFundName.trim()) {
      toast.error('Please enter fund name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/funds?admin_email=${userEmail}`, { name: newFundName.trim() });
      toast.success('Fund created successfully!');
      setNewFundName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fund');
    }
  };

  const handleUpdateFund = async (fundId) => {
    if (!editFundName.trim()) {
      toast.error('Please enter fund name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(`${API}/admin/funds/${fundId}?admin_email=${userEmail}`, { name: editFundName.trim() });
      toast.success('Fund updated successfully!');
      setEditingFund(null);
      setEditFundName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fund');
    }
  };

  const handleDeleteFund = async (fundId) => {
    if (!window.confirm('Are you sure you want to delete this fund?')) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.delete(`${API}/admin/funds/${fundId}?admin_email=${userEmail}`);
      toast.success('Fund deleted successfully!');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete fund');
    }
  };

  const handleAddFundInDealForm = async () => {
    if (!newFundInDealForm.trim()) {
      toast.error('Please enter fund name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.post(`${API}/admin/funds?admin_email=${userEmail}`, { name: newFundInDealForm.trim() });
      toast.success('Fund created successfully!');
      setFunds([...funds, response.data.fund]);
      const currentFunds = newDeal.funds || [];
      setNewDeal({ ...newDeal, funds: [...currentFunds, newFundInDealForm.trim()] });
      setNewFundInDealForm('');
      setShowAddFundInDealForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fund');
    }
  };

  const toggleFundSelection = (fundName) => {
    const currentFunds = newDeal.funds || [];
    if (currentFunds.includes(fundName)) {
      setNewDeal({ ...newDeal, funds: currentFunds.filter(f => f !== fundName) });
    } else {
      setNewDeal({ ...newDeal, funds: [...currentFunds, fundName] });
    }
  };

  const toggleEditFundSelection = (fundName) => {
    const currentFunds = editingDeal?.funds || [];
    if (currentFunds.includes(fundName)) {
      setEditingDeal({ ...editingDeal, funds: currentFunds.filter(f => f !== fundName) });
    } else {
      setEditingDeal({ ...editingDeal, funds: [...currentFunds, fundName] });
    }
  };

  // Angel Networks management functions
  const handleCreateNetwork = async () => {
    if (!newNetworkName.trim()) {
      toast.error('Please enter angel network name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/angel-networks?admin_email=${userEmail}`, { name: newNetworkName.trim() });
      toast.success('Angel Network created successfully!');
      setNewNetworkName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create angel network');
    }
  };

  const handleUpdateNetwork = async (networkId) => {
    if (!editNetworkName.trim()) {
      toast.error('Please enter angel network name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(`${API}/admin/angel-networks/${networkId}?admin_email=${userEmail}`, { name: editNetworkName.trim() });
      toast.success('Angel Network updated successfully!');
      setEditingNetwork(null);
      setEditNetworkName('');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update angel network');
    }
  };

  const handleDeleteNetwork = async (networkId) => {
    if (!window.confirm('Are you sure you want to delete this angel network?')) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.delete(`${API}/admin/angel-networks/${networkId}?admin_email=${userEmail}`);
      toast.success('Angel Network deleted successfully!');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete angel network');
    }
  };

  const handleAddNetworkInDealForm = async () => {
    if (!newNetworkInDealForm.trim()) {
      toast.error('Please enter angel network name');
      return;
    }
    const userEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.post(`${API}/admin/angel-networks?admin_email=${userEmail}`, { name: newNetworkInDealForm.trim() });
      toast.success('Angel Network created successfully!');
      setAngelNetworks([...angelNetworks, response.data.angel_network]);
      const currentNetworks = newDeal.angel_networks || [];
      setNewDeal({ ...newDeal, angel_networks: [...currentNetworks, newNetworkInDealForm.trim()] });
      setNewNetworkInDealForm('');
      setShowAddNetworkInDealForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create angel network');
    }
  };

  // Dynamic Entity Form save handlers
  const handleSaveBankerEntity = async (formData, entityId) => {
    const userEmail = localStorage.getItem('user_email');
    try {
      if (entityId) {
        await axios.put(`${API}/admin/investment-bankers/${entityId}?admin_email=${userEmail}`, formData);
        toast.success('Investment Banker updated successfully!');
      } else {
        await axios.post(`${API}/admin/investment-bankers?admin_email=${userEmail}`, formData);
        toast.success('Investment Banker created successfully!');
      }
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save investment banker');
      throw error;
    }
  };

  const handleSaveFundEntity = async (formData, entityId) => {
    const userEmail = localStorage.getItem('user_email');
    try {
      if (entityId) {
        await axios.put(`${API}/admin/funds/${entityId}?admin_email=${userEmail}`, formData);
        toast.success('Fund updated successfully!');
      } else {
        await axios.post(`${API}/admin/funds?admin_email=${userEmail}`, formData);
        toast.success('Fund created successfully!');
      }
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fund');
      throw error;
    }
  };

  const handleSaveNetworkEntity = async (formData, entityId) => {
    const userEmail = localStorage.getItem('user_email');
    try {
      if (entityId) {
        await axios.put(`${API}/admin/angel-networks/${entityId}?admin_email=${userEmail}`, formData);
        toast.success('Angel Network updated successfully!');
      } else {
        await axios.post(`${API}/admin/angel-networks?admin_email=${userEmail}`, formData);
        toast.success('Angel Network created successfully!');
      }
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save angel network');
      throw error;
    }
  };

  const toggleNetworkSelection = (networkName) => {
    const currentNetworks = newDeal.angel_networks || [];
    if (currentNetworks.includes(networkName)) {
      setNewDeal({ ...newDeal, angel_networks: currentNetworks.filter(n => n !== networkName) });
    } else {
      setNewDeal({ ...newDeal, angel_networks: [...currentNetworks, networkName] });
    }
  };

  const toggleEditNetworkSelection = (networkName) => {
    const currentNetworks = editingDeal?.angel_networks || [];
    if (currentNetworks.includes(networkName)) {
      setEditingDeal({ ...editingDeal, angel_networks: currentNetworks.filter(n => n !== networkName) });
    } else {
      setEditingDeal({ ...editingDeal, angel_networks: [...currentNetworks, networkName] });
    }
  };

  const handleDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and PPT files are allowed');
      return;
    }

    setUploadingDeck(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/upload-deck`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNewDeal({ ...newDeal, deck_url: response.data.file_url });
      toast.success('Deck uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload deck');
    } finally {
      setUploadingDeck(false);
    }
  };

  // Handler for uploading deck in Edit Deal dialog
  const handleEditDeckUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and PPT files are allowed');
      return;
    }

    setUploadingEditDeck(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/admin/upload-deck`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setEditingDeal({ ...editingDeal, deck_url: response.data.file_url });
      toast.success('Deck uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload deck');
    } finally {
      setUploadingEditDeck(false);
    }
  };

  // Founder management for Add Deal
  const handleDealFounderChange = (index, field, value) => {
    let processedValue = value;
    if (field === 'phone') {
      processedValue = formatPhoneInput(value);
    }
    
    const newFounders = [...newDeal.founders];
    newFounders[index] = { ...newFounders[index], [field]: processedValue };
    setNewDeal(prev => ({ ...prev, founders: newFounders }));
    
    // Real-time validation
    const newErrors = { ...dealFormErrors };
    const errorKey = `founder_${index}_${field}`;
    
    if (field === 'email' && processedValue.trim()) {
      if (!isValidEmail(processedValue)) {
        newErrors[errorKey] = 'Invalid email format';
      } else {
        delete newErrors[errorKey];
      }
    } else if (field === 'phone' && processedValue.trim()) {
      if (!isValidPhone(processedValue)) {
        newErrors[errorKey] = 'Phone must be 10-15 digits';
      } else {
        delete newErrors[errorKey];
      }
    } else if (field === 'linkedin' && processedValue.trim()) {
      if (!isValidLinkedIn(processedValue)) {
        newErrors[errorKey] = 'Must be a valid LinkedIn URL';
      } else {
        delete newErrors[errorKey];
      }
    } else {
      delete newErrors[errorKey];
    }
    
    setDealFormErrors(newErrors);
  };

  const addDealFounder = () => {
    setNewDeal(prev => ({
      ...prev,
      founders: [...prev.founders, { name: '', email: '', phone: '', linkedin: '' }]
    }));
  };

  const removeDealFounder = (index) => {
    if (newDeal.founders.length > 1) {
      setNewDeal(prev => ({
        ...prev,
        founders: prev.founders.filter((_, i) => i !== index)
      }));
    }
  };

  const handleDealInputChange = (field, value) => {
    setNewDeal(prev => ({ ...prev, [field]: value }));
    
    const newErrors = { ...dealFormErrors };
    
    if (field === 'website' && value.trim()) {
      if (!isValidUrl(value)) {
        newErrors.website = 'Must be a valid URL (start with https://)';
      } else {
        delete newErrors.website;
      }
    } else if (field === 'demo_link' && value.trim()) {
      if (!isValidUrl(value)) {
        newErrors.demo_link = 'Must be a valid URL';
      } else {
        delete newErrors.demo_link;
      }
    } else {
      delete newErrors[field];
    }
    
    setDealFormErrors(newErrors);
  };

  const handleDealRevenueChange = (monthKey, value) => {
    const numericValue = value.replace(/[^\d]/g, '');
    setNewDeal(prev => ({
      ...prev,
      revenue: { ...prev.revenue, [monthKey]: numericValue ? parseInt(numericValue) : 0 }
    }));
  };

  // Previous Fundraise handlers for Add Deal
  const addDealPreviousFundraise = () => {
    setNewDeal(prev => ({
      ...prev,
      previous_fundraises: [...(prev.previous_fundraises || []), { type: '', valuation: '', grant_name: '', amount: '' }]
    }));
  };

  const removeDealPreviousFundraise = (index) => {
    setNewDeal(prev => ({
      ...prev,
      previous_fundraises: prev.previous_fundraises.filter((_, i) => i !== index)
    }));
  };

  const handleDealPreviousFundraiseChange = (index, field, value) => {
    let processedValue = value;
    if (field === 'valuation' || field === 'amount') {
      processedValue = value.replace(/[^\d]/g, '');
    }
    
    const newFundraises = [...(newDeal.previous_fundraises || [])];
    newFundraises[index] = { ...newFundraises[index], [field]: processedValue };
    
    if (field === 'type') {
      if (value === 'Fundraise') {
        newFundraises[index].grant_name = '';
      } else if (value === 'Grant') {
        newFundraises[index].valuation = '';
      }
    }
    
    setNewDeal(prev => ({ ...prev, previous_fundraises: newFundraises }));
  };

  const validateDealForm = () => {
    const errors = {};
    
    // Admin forms: Only validate format if value is provided (no mandatory fields)
    
    // Website format validation (only if provided)
    if (newDeal.website.trim() && !isValidUrl(newDeal.website)) {
      errors.website = 'Invalid URL format';
    }
    
    // Validate founders format (only if values provided)
    newDeal.founders.forEach((founder, index) => {
      if (founder.email.trim() && !isValidEmail(founder.email)) {
        errors[`founder_${index}_email`] = 'Invalid email format';
      }
      if (founder.phone.trim() && !isValidPhone(founder.phone)) {
        errors[`founder_${index}_phone`] = 'Invalid phone (10-15 digits)';
      }
      if (founder.linkedin.trim() && !isValidLinkedIn(founder.linkedin)) {
        errors[`founder_${index}_linkedin`] = 'Must be linkedin.com URL';
      }
    });
    
    // One liner length check (only if provided)
    if (newDeal.one_liner.trim() && newDeal.one_liner.length > 50) {
      errors.one_liner = 'Max 50 characters';
    }
    
    // Demo link format validation (only if provided)
    if (newDeal.demo_link.trim() && !isValidUrl(newDeal.demo_link)) {
      errors.demo_link = 'Invalid URL format';
    }
    
    // Terms conditional validation (only if terms_type is selected)
    if (newDeal.terms_type === 'Valuation' && !newDeal.valuation_amount) {
      errors.valuation_amount = 'Valuation is required when Valuation type is selected';
    }
    if (newDeal.terms_type === 'SAFE' && !newDeal.safe_terms.trim()) {
      errors.safe_terms = 'SAFE terms are required when SAFE type is selected';
    }
    if (newDeal.terms_type === 'Other' && !newDeal.other_terms.trim()) {
      errors.other_terms = 'Terms are required when Other type is selected';
    }
    
    setDealFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddDeal = async (e) => {
    e.preventDefault();

    if (!validateDealForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    // Check for duplicate name
    if (isDuplicateName) {
      toast.error('A deal with this name already exists. Please choose a different name.');
      return;
    }

    const userEmail = localStorage.getItem('user_email');
    try {
      // Create the deal with all startup form fields
      await axios.post(`${API}/admin/deals?admin_email=${userEmail}`, {
        name: newDeal.brand_name, // Use brand_name as deal name
        legal_name: newDeal.legal_name,
        brand_name: newDeal.brand_name,
        website: newDeal.website,
        location: newDeal.location,
        founders: newDeal.founders,
        stage: newDeal.stage,
        category: newDeal.category,
        one_liner: newDeal.one_liner,
        description: newDeal.description,
        product: newDeal.product,
        product_pricing: newDeal.product_pricing,
        revenue: newDeal.revenue,
        previous_fundraises: newDeal.previous_fundraises || [],
        deck_url: newDeal.deck_url,
        demo_link: newDeal.demo_link,
        amount_raising: newDeal.amount_to_raise ? parseInt(newDeal.amount_to_raise) : 0,
        terms_type: newDeal.terms_type,
        valuation_amount: newDeal.valuation_amount ? parseInt(newDeal.valuation_amount) : null,
        safe_terms: newDeal.safe_terms,
        other_terms: newDeal.other_terms,
        // Entity associations
        event_category: newDeal.event_category || '',
        investment_bankers: newDeal.investment_bankers || [],
        funds: newDeal.funds || [],
        angel_networks: newDeal.angel_networks || [],
        type: 'Events' // Default type
      });

      toast.success('Deal added successfully!');
      // Reset form to initial state
      setNewDeal({
        legal_name: '',
        brand_name: '',
        website: '',
        location: '',
        founders: [{ name: '', email: '', phone: '', linkedin: '' }],
        stage: '',
        category: '',
        one_liner: '',
        description: '',
        product: '',
        product_pricing: '',
        revenue: {
          month1: 0, month2: 0, month3: 0, month4: 0, month5: 0,
          month1_label: revenueMonths[0], month2_label: revenueMonths[1], 
          month3_label: revenueMonths[2], month4_label: revenueMonths[3], month5_label: revenueMonths[4]
        },
        previous_fundraises: [],
        deck_url: '',
        demo_link: '',
        amount_to_raise: '',
        terms_type: '',
        valuation_amount: '',
        safe_terms: '',
        other_terms: '',
        event_category: '',
        investment_bankers: [],
        funds: [],
        angel_networks: []
      });
      setDealFormErrors({});
      setIsDuplicateName(false);
      setIsCheckingName(false);
      fetchData(userEmail);
      
      // Close the dialog by triggering a click on the close button
      setTimeout(() => {
        const closeBtn = document.querySelector('[role="dialog"] button[aria-label="Close"]');
        if (closeBtn) closeBtn.click();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add deal');
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) {
      return;
    }

    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.delete(`${API}/admin/deals/${dealId}?admin_email=${userEmail}`);
      toast.success('Deal deleted successfully!');
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deal');
    }
  };

  const handleEditDeal = (deal) => {
    // Initialize with all startup form fields, using existing data or defaults
    setEditingDeal({
      ...deal,
      legal_name: deal.legal_name || deal.name || '',
      brand_name: deal.brand_name || deal.name || '',
      website: deal.website || '',
      location: deal.location || '',
      founders: deal.founders || [{ name: '', email: '', phone: '', linkedin: '' }],
      stage: deal.stage || '',
      category: deal.category || '',
      one_liner: deal.one_liner || deal.description || '',
      description: deal.description || '',
      product: deal.product || '',
      product_pricing: deal.product_pricing || '',
      revenue: deal.revenue || {
        month1: 0, month2: 0, month3: 0, month4: 0, month5: 0,
        month1_label: revenueMonths[0], month2_label: revenueMonths[1], 
        month3_label: revenueMonths[2], month4_label: revenueMonths[3], month5_label: revenueMonths[4]
      },
      previous_fundraises: deal.previous_fundraises || [],
      deck_url: deal.deck_url || '',
      demo_link: deal.demo_link || '',
      amount_to_raise: deal.amount_raising || deal.amount_to_raise || '',
      terms_type: deal.terms_type || '',
      valuation_amount: deal.valuation_amount || '',
      safe_terms: deal.safe_terms || '',
      other_terms: deal.other_terms || '',
      event_category: deal.event_category || '',
      investment_bankers: deal.investment_bankers || [],
      funds: deal.funds || [],
      angel_networks: deal.angel_networks || [],
      custom_fields: deal.custom_fields || []
    });
    setEditFormErrors({});
    setShowEditDealDialog(true);
  };

  const handleDownloadPDF = async (deal) => {
    const userEmail = localStorage.getItem('user_email');
    try {
      toast.info('Generating PDF...');
      // Pass base URL for the CTA link in the PDF
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${API}/deals/${deal.id}/pdf?requester_email=${encodeURIComponent(userEmail)}&base_url=${encodeURIComponent(baseUrl)}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const brandName = deal.brand_name || deal.name || 'Deal';
      link.setAttribute('download', `${brandName}_Deal_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to generate PDF');
      console.error('PDF download error:', error);
    }
  };

  const handleCopyShareLink = (deal) => {
    // Use slug (URL-friendly name) instead of ID
    const slug = deal.slug || deal.brand_name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || deal.id;
    const shareUrl = `${window.location.origin}/share/${slug}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success('Share link copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Share link copied to clipboard!');
    });
  };

  // Edit form handlers
  const handleEditFounderChange = (index, field, value) => {
    let processedValue = value;
    if (field === 'phone') {
      processedValue = formatPhoneInput(value);
    }
    
    const newFounders = [...(editingDeal.founders || [])];
    newFounders[index] = { ...newFounders[index], [field]: processedValue };
    setEditingDeal(prev => ({ ...prev, founders: newFounders }));
    
    // Real-time validation
    const newErrors = { ...editFormErrors };
    const errorKey = `edit_founder_${index}_${field}`;
    
    if (field === 'email' && processedValue.trim()) {
      if (!isValidEmail(processedValue)) {
        newErrors[errorKey] = 'Invalid email format';
      } else {
        delete newErrors[errorKey];
      }
    } else if (field === 'phone' && processedValue.trim()) {
      if (!isValidPhone(processedValue)) {
        newErrors[errorKey] = 'Phone must be 10-15 digits';
      } else {
        delete newErrors[errorKey];
      }
    } else if (field === 'linkedin' && processedValue.trim()) {
      if (!isValidLinkedIn(processedValue)) {
        newErrors[errorKey] = 'Must be a valid LinkedIn URL';
      } else {
        delete newErrors[errorKey];
      }
    } else {
      delete newErrors[errorKey];
    }
    
    setEditFormErrors(newErrors);
  };

  const addEditFounder = () => {
    setEditingDeal(prev => ({
      ...prev,
      founders: [...(prev.founders || []), { name: '', email: '', phone: '', linkedin: '' }]
    }));
  };

  const removeEditFounder = (index) => {
    if ((editingDeal.founders || []).length > 1) {
      setEditingDeal(prev => ({
        ...prev,
        founders: prev.founders.filter((_, i) => i !== index)
      }));
    }
  };

  const handleEditInputChange = (field, value) => {
    setEditingDeal(prev => ({ ...prev, [field]: value }));
    
    const newErrors = { ...editFormErrors };
    
    // Real-time validation with correct error keys for DynamicDealForm
    if (field === 'website' && value.trim()) {
      if (!isValidUrl(value)) {
        newErrors.website = 'Must be a valid URL (start with https://)';
      } else {
        delete newErrors.website;
      }
    } else if (field === 'demo_link' && value.trim()) {
      if (!isValidUrl(value)) {
        newErrors.demo_link = 'Must be a valid URL';
      } else {
        delete newErrors.demo_link;
      }
    } else if (field === 'one_liner' && value.length > 50) {
      newErrors.one_liner = 'Max 50 characters';
    } else {
      // Clear error for this field
      delete newErrors[field];
    }
    
    setEditFormErrors(newErrors);
  };

  const handleEditRevenueChange = (monthKey, value) => {
    const numericValue = value.replace(/[^\d]/g, '');
    setEditingDeal(prev => ({
      ...prev,
      revenue: { ...prev.revenue, [monthKey]: numericValue ? parseInt(numericValue) : 0 }
    }));
  };

  // Previous Fundraise handlers for Edit Deal
  const addEditPreviousFundraise = () => {
    setEditingDeal(prev => ({
      ...prev,
      previous_fundraises: [...(prev.previous_fundraises || []), { type: '', valuation: '', grant_name: '', amount: '' }]
    }));
  };

  const removeEditPreviousFundraise = (index) => {
    setEditingDeal(prev => ({
      ...prev,
      previous_fundraises: prev.previous_fundraises.filter((_, i) => i !== index)
    }));
  };

  const handleEditPreviousFundraiseChange = (index, field, value) => {
    let processedValue = value;
    if (field === 'valuation' || field === 'amount') {
      processedValue = value.replace(/[^\d]/g, '');
    }
    
    const newFundraises = [...(editingDeal.previous_fundraises || [])];
    newFundraises[index] = { ...newFundraises[index], [field]: processedValue };
    
    if (field === 'type') {
      if (value === 'Fundraise') {
        newFundraises[index].grant_name = '';
      } else if (value === 'Grant') {
        newFundraises[index].valuation = '';
      }
    }
    
    setEditingDeal(prev => ({ ...prev, previous_fundraises: newFundraises }));
  };

  const validateEditForm = () => {
    const errors = {};
    
    // Admin forms: Only validate format if value is provided (no mandatory fields)
    // Error keys must match field_key values for DynamicDealForm to show red borders
    
    // Website format validation (only if provided)
    if (editingDeal.website?.trim() && !isValidUrl(editingDeal.website)) {
      errors.website = 'Invalid URL format';
    }
    
    // Validate founders format (only if values provided)
    let foundersHaveErrors = false;
    (editingDeal.founders || []).forEach((founder, index) => {
      if (founder.email?.trim() && !isValidEmail(founder.email)) {
        errors[`founder_${index}_email`] = 'Invalid email format';
        foundersHaveErrors = true;
      }
      if (founder.phone?.trim() && !isValidPhone(founder.phone)) {
        errors[`founder_${index}_phone`] = 'Invalid phone (10-15 digits)';
        foundersHaveErrors = true;
      }
      if (founder.linkedin?.trim() && !isValidLinkedIn(founder.linkedin)) {
        errors[`founder_${index}_linkedin`] = 'Must be linkedin.com URL';
        foundersHaveErrors = true;
      }
    });
    // Set error on founders field so DynamicDealForm can show red styling
    if (foundersHaveErrors) {
      errors.founders = 'Please fix founder field errors';
    }
    
    // One liner length check (only if provided)
    if (editingDeal.one_liner?.trim() && editingDeal.one_liner.length > 50) {
      errors.one_liner = 'Max 50 characters';
    }
    
    // Demo link format validation (only if provided)
    if (editingDeal.demo_link?.trim() && !isValidUrl(editingDeal.demo_link)) {
      errors.demo_link = 'Invalid URL format';
    }
    
    // Terms conditional validation (only if terms_type is selected)
    if (editingDeal.terms_type === 'Valuation' && !editingDeal.valuation_amount) {
      errors.valuation_amount = 'Valuation is required when Valuation type is selected';
    }
    if (editingDeal.terms_type === 'SAFE' && !editingDeal.safe_terms?.trim()) {
      errors.safe_terms = 'SAFE terms are required when SAFE type is selected';
    }
    if (editingDeal.terms_type === 'Other' && !editingDeal.other_terms?.trim()) {
      errors.other_terms = 'Terms are required when Other type is selected';
    }
    
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateDeal = async () => {
    if (!editingDeal) return;
    
    if (!validateEditForm()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(`${API}/admin/deals/${editingDeal.id}?admin_email=${userEmail}`, {
        name: editingDeal.brand_name,
        legal_name: editingDeal.legal_name,
        brand_name: editingDeal.brand_name,
        website: editingDeal.website,
        location: editingDeal.location,
        founders: editingDeal.founders,
        stage: editingDeal.stage,
        category: editingDeal.category,
        one_liner: editingDeal.one_liner,
        description: editingDeal.description,
        product: editingDeal.product,
        product_pricing: editingDeal.product_pricing,
        revenue: editingDeal.revenue,
        previous_fundraises: editingDeal.previous_fundraises || [],
        deck_url: editingDeal.deck_url,
        demo_link: editingDeal.demo_link,
        amount_raising: editingDeal.amount_to_raise ? parseInt(editingDeal.amount_to_raise) : 0,
        terms_type: editingDeal.terms_type,
        valuation_amount: editingDeal.valuation_amount ? parseInt(editingDeal.valuation_amount) : null,
        safe_terms: editingDeal.safe_terms,
        other_terms: editingDeal.other_terms,
        event_category: editingDeal.event_category || '',
        investment_bankers: editingDeal.investment_bankers || [],
        funds: editingDeal.funds || [],
        angel_networks: editingDeal.angel_networks || [],
        type: editingDeal.type || 'Events',
        custom_fields: editingDeal.custom_fields || []
      });
      toast.success('Deal updated successfully!');
      setShowEditDealDialog(false);
      setEditingDeal(null);
      setEditFormErrors({});
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update deal');
    }
  };

  const checkDealName = async (name) => {
    if (!name || name.trim().length === 0) {
      setIsDuplicateName(false);
      setIsCheckingName(false);
      return;
    }

    const userEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.get(`${API}/admin/check-deal-name?name=${encodeURIComponent(name)}&admin_email=${userEmail}`);
      setIsDuplicateName(response.data.exists);
    } catch (error) {
      console.error('Error checking deal name:', error);
    } finally {
      setIsCheckingName(false);
    }
  };

  const handleDealNameChange = (value) => {
    // Only check for duplicate name, don't set state here
    // State is already set by handleDealInputChange
    
    // Clear existing timeout
    if (nameCheckTimeout) {
      clearTimeout(nameCheckTimeout);
    }
    
    // Set checking state
    if (value.trim().length > 0) {
      setIsCheckingName(true);
      
      // Set new timeout for 80ms
      const timeout = setTimeout(() => {
        checkDealName(value);
      }, 80);
      
      setNameCheckTimeout(timeout);
    } else {
      setIsDuplicateName(false);
      setIsCheckingName(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();

    if (!inviteData.first_name || !inviteData.last_name || !inviteData.email) {
      toast.error('Please fill in all fields');
      return;
    }

    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.post(`${API}/admin/invite?inviter_email=${userEmail}`, inviteData);
      toast.success('Invitation sent successfully!');
      setInviteData({ first_name: '', last_name: '', email: '' });
      
      // Close the dialog by triggering a click on the close button
      setTimeout(() => {
        const closeBtn = document.querySelector('[role="dialog"] button[aria-label="Close"]');
        if (closeBtn) closeBtn.click();
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleViewDealCommitments = async (deal) => {
    const userEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.get(`${API}/deals/${deal.id}/commitments?requester_email=${userEmail}`);
      setSelectedDeal(deal);
      setDealCommitments(response.data);
      setShowCommitmentsDialog(true);
    } catch (error) {
      toast.error('Failed to load commitments');
    }
  };

  const handleViewUserCommitments = async (user) => {
    try {
      const adminEmail = localStorage.getItem('user_email');
      const response = await axios.get(`${API}/admin/users/${encodeURIComponent(user.email)}/commitments?admin_email=${adminEmail}`);
      setSelectedUser(user);
      setUserCommitments(response.data);
      setShowUserCommitmentsDialog(true);
    } catch (error) {
      toast.error('Failed to load user commitments');
    }
  };

  // Investment Management Functions
  const handleRefreshDealCommitments = async () => {
    if (!selectedDeal) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.get(`${API}/deals/${selectedDeal.id}/commitments?requester_email=${userEmail}`);
      setDealCommitments(response.data);
      // Also refresh deals to update commitment count
      fetchData(userEmail);
    } catch (error) {
      toast.error('Failed to refresh commitments');
    }
  };

  const handleRefreshUserCommitments = async () => {
    if (!selectedUser) return;
    const adminEmail = localStorage.getItem('user_email');
    try {
      const response = await axios.get(`${API}/admin/users/${encodeURIComponent(selectedUser.email)}/commitments?admin_email=${adminEmail}`);
      setUserCommitments(response.data);
    } catch (error) {
      toast.error('Failed to refresh commitments');
    }
  };

  const handleEditCommitment = (commitment, fromDealView = true) => {
    setEditingCommitment({ ...commitment, fromDealView });
    setShowEditCommitmentDialog(true);
  };

  const handleDeleteCommitment = async (commitment) => {
    if (!window.confirm(`Are you sure you want to delete this ${formatINR(commitment.amount_inr)} commitment?`)) return;
    
    const adminEmail = localStorage.getItem('user_email');
    try {
      await axios.delete(`${API}/admin/commitments/${commitment.id}?admin_email=${adminEmail}`);
      toast.success('Commitment deleted successfully');
      // Refresh the appropriate view
      if (showCommitmentsDialog) {
        handleRefreshDealCommitments();
      }
      if (showUserCommitmentsDialog) {
        handleRefreshUserCommitments();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete commitment');
    }
  };

  const handleRoleChange = async (userEmail, newRole) => {
    try {
      const requesterEmail = localStorage.getItem('userEmail');
      await axios.put(`${API}/admin/users/${encodeURIComponent(userEmail)}/role?new_role=${newRole}&requester_email=${encodeURIComponent(requesterEmail)}`);
      toast.success(`Role updated to ${newRole}`);
      // Refresh users list
      const response = await axios.get(`${API}/admin/users?requester_email=${requesterEmail}`);
      setAllUsers(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // User Associations management functions
  const handleOpenUserAssociations = (user) => {
    setEditingUserAssociations(user);
    setUserAssociationsData({
      investment_bankers: user.investment_bankers || [],
      funds: user.funds || [],
      angel_networks: user.angel_networks || []
    });
    setShowUserAssociationsDialog(true);
  };

  // Edit Profile functions
  const handleOpenEditProfile = (user) => {
    setEditingProfileUser(user);
    setEditProfileData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      capital_deployment: user.capital_deployment ? String(user.capital_deployment) : ''
    });
    setShowEditProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!editingProfileUser) return;
    const userEmail = localStorage.getItem('user_email');
    try {
      const payload = {};
      if (editProfileData.first_name) payload.first_name = editProfileData.first_name;
      if (editProfileData.last_name) payload.last_name = editProfileData.last_name;
      if (editProfileData.phone) payload.phone = editProfileData.phone;
      if (editProfileData.capital_deployment) payload.capital_deployment = parseInt(editProfileData.capital_deployment);

      await axios.put(
        `${API}/admin/users/${encodeURIComponent(editingProfileUser.email)}/profile?requester_email=${encodeURIComponent(userEmail)}`,
        payload
      );
      toast.success('Profile updated successfully!');
      setShowEditProfileDialog(false);
      setEditingProfileUser(null);
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleUpdateUserAssociations = async () => {
    if (!editingUserAssociations) return;
    
    const userEmail = localStorage.getItem('user_email');
    try {
      await axios.put(
        `${API}/admin/users/${encodeURIComponent(editingUserAssociations.email)}/associations?requester_email=${encodeURIComponent(userEmail)}`,
        userAssociationsData
      );
      toast.success('Associations updated successfully!');
      setShowUserAssociationsDialog(false);
      setEditingUserAssociations(null);
      fetchData(userEmail);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update associations');
    }
  };

  const toggleUserBankerSelection = (bankerName) => {
    const currentBankers = userAssociationsData.investment_bankers || [];
    if (currentBankers.includes(bankerName)) {
      setUserAssociationsData({
        ...userAssociationsData,
        investment_bankers: currentBankers.filter(b => b !== bankerName)
      });
    } else {
      setUserAssociationsData({
        ...userAssociationsData,
        investment_bankers: [...currentBankers, bankerName]
      });
    }
  };

  const toggleUserFundSelection = (fundName) => {
    const currentFunds = userAssociationsData.funds || [];
    if (currentFunds.includes(fundName)) {
      setUserAssociationsData({
        ...userAssociationsData,
        funds: currentFunds.filter(f => f !== fundName)
      });
    } else {
      setUserAssociationsData({
        ...userAssociationsData,
        funds: [...currentFunds, fundName]
      });
    }
  };

  const toggleUserNetworkSelection = (networkName) => {
    const currentNetworks = userAssociationsData.angel_networks || [];
    if (currentNetworks.includes(networkName)) {
      setUserAssociationsData({
        ...userAssociationsData,
        angel_networks: currentNetworks.filter(n => n !== networkName)
      });
    } else {
      setUserAssociationsData({
        ...userAssociationsData,
        angel_networks: [...currentNetworks, networkName]
      });
    }
  };

  // Filter out super_admin users from the list - they shouldn't be visible
  const approvedUsers = allUsers.filter(u => u.status === 'approved' && u.role !== 'super_admin');

  // Filtered data based on search queries and filters
  const filteredDeals = deals.filter(deal => {
    // Search filter - search across multiple fields
    const searchTerm = searchDeals.toLowerCase();
    const matchesSearch = 
      deal.name?.toLowerCase().includes(searchTerm) ||
      deal.brand_name?.toLowerCase().includes(searchTerm) ||
      deal.one_liner?.toLowerCase().includes(searchTerm) ||
      deal.type?.toLowerCase().includes(searchTerm) ||
      deal.event_category?.toLowerCase().includes(searchTerm);
    
    if (!matchesSearch) return false;
    
    // Type filter
    if (filterDealType !== 'all' && deal.type !== filterDealType) return false;
    
    // Event filter (only when type is Events)
    if (filterDealType === 'Events' && filterEvent !== 'all') {
      if (deal.event_category !== filterEvent) return false;
    }
    
    // Investment Banker filter
    if (filterBanker !== 'all') {
      if (!deal.investment_bankers?.includes(filterBanker)) return false;
    }
    
    // Fund filter
    if (filterFund !== 'all') {
      if (!deal.funds?.includes(filterFund)) return false;
    }
    
    // Angel Network filter
    if (filterNetwork !== 'all') {
      if (!deal.angel_networks?.includes(filterNetwork)) return false;
    }
    
    return true;
  });

  const filteredEvents = events.filter(event =>
    event.name?.toLowerCase().includes(searchEvents.toLowerCase())
  );

  const filteredBankers = investmentBankers.filter(banker =>
    banker.name?.toLowerCase().includes(searchBankers.toLowerCase())
  );

  const filteredFunds = funds.filter(fund =>
    fund.name?.toLowerCase().includes(searchFunds.toLowerCase())
  );

  const filteredNetworks = angelNetworks.filter(network =>
    network.name?.toLowerCase().includes(searchNetworks.toLowerCase())
  );

  const filteredApprovedUsers = approvedUsers.filter(user => {
    const matchesSearch = (
      user.email?.toLowerCase().includes(searchUsers.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchUsers.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchUsers.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchUsers.toLowerCase())
    );
    const matchesRole = filterUserRole === 'all' || user.role === filterUserRole;
    return matchesSearch && matchesRole;
  });

  const filteredPendingMembers = pendingMembers.filter(user =>
    user.email?.toLowerCase().includes(searchUsers.toLowerCase()) ||
    user.first_name?.toLowerCase().includes(searchUsers.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchUsers.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <p className="text-[#64748B]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Back to Deals - Left Side */}
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="!bg-transparent text-[#64748B] hover:text-[#0F172A] hover:!bg-transparent"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Deals
            </Button>
            
            {/* Admin Dashboard Info - Right Side */}
            <div className="text-right">
              <div className="flex items-center justify-end gap-3">
                <h1 className="font-heading text-xl font-bold text-brand">
                  {userRole === 'super_admin' ? 'SUPER ADMIN DASHBOARD' : 
                   userRole === 'admin' ? 'ADMIN DASHBOARD' : 'TEAM DASHBOARD'}
                </h1>
                {userRole && (
                  <Badge className={
                    userRole === 'super_admin' ? 'bg-purple-600' :
                    userRole === 'admin' ? 'bg-brand' : 'bg-[#10B981]'
                  }>
                    {userRole === 'super_admin' ? 'SUPER ADMIN' : userRole.toUpperCase()}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-[#64748B]">
                <span>{branding.platformName}</span>
                <span className="text-[10px] text-gray-400 tracking-wide ml-2">Powered by InMeSta Ecosystem</span>
                {currentUser && ` • ${currentUser.first_name} ${currentUser.last_name}`}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <Card className="border-[#E2E8F0] shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#F1F5F9] rounded-md flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-brand" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B]">Deals</p>
                  <p className="font-heading text-2xl font-bold text-[#0F172A]">{deals.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FEF3C7] rounded-md flex items-center justify-center">
                  <Clock className="h-6 w-6 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B]">Pending</p>
                  <p className="font-heading text-2xl font-bold text-[#0F172A]">{pendingMembers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#E2E8F0] shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#ECFDF5] rounded-md flex items-center justify-center">
                  <UserCheck className="h-6 w-6 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-sm text-[#64748B]">Angels</p>
                  <p className="font-heading text-2xl font-bold text-[#0F172A]">{approvedUsers.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#E2E8F0] mb-6">
            <TabsTrigger 
              value="deals" 
              className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
            >
              Deals ({deals.length})
            </TabsTrigger>
            <TabsTrigger 
              value="events"
              className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
            >
              Events ({events.length})
            </TabsTrigger>
            <TabsTrigger 
              value="bankers"
              className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
            >
              Investment Bankers ({investmentBankers.length})
            </TabsTrigger>
            <TabsTrigger 
              value="funds"
              className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
            >
              Funds ({funds.length})
            </TabsTrigger>
            <TabsTrigger 
              value="networks"
              className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
            >
              Angel Networks ({angelNetworks.length})
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
            >
              Angels ({approvedUsers.length + pendingMembers.length})
            </TabsTrigger>
            <TabsTrigger 
              value="forms"
              className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
            >
              <Settings2 className="h-4 w-4 mr-1" />
              Forms
            </TabsTrigger>
          </TabsList>

          {/* Deals Tab */}
          <TabsContent value="deals">
            {/* Sub-tabs: Deals and Approvals */}
            <Tabs value={dealsSubTab} onValueChange={setDealsSubTab}>
              <TabsList className="bg-white border border-[#E2E8F0] mb-6">
                <TabsTrigger 
                  value="deals" 
                  className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
                >
                  Deals
                </TabsTrigger>
                <TabsTrigger 
                  value="approvals" 
                  className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
                >
                  Approvals
                </TabsTrigger>
              </TabsList>

              {/* Deals Sub-tab */}
              <TabsContent value="deals">
                {/* Search Bar and Filters */}
                <div className="mb-4 space-y-3">
                  <div className="flex gap-4 items-center">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search deals by name, type, or event..."
                        value={searchDeals}
                        onChange={(e) => setSearchDeals(e.target.value)}
                        className="pl-10 bg-white border-[#E2E8F0]"
                      />
                    </div>
                    {(userRole === 'admin' || userRole === 'super_admin') && (
                    <Dialog onOpenChange={(open) => {
                      if (!open) {
                        // Clear form and validation states when dialog closes
                        setNewDeal({
                          legal_name: '',
                          brand_name: '',
                          website: '',
                          location: '',
                          founders: [{ name: '', email: '', phone: '', linkedin: '' }],
                          stage: '',
                          category: '',
                          one_liner: '',
                          description: '',
                          product: '',
                          product_pricing: '',
                          revenue: {
                            month1: 0, month2: 0, month3: 0, month4: 0, month5: 0,
                            month1_label: revenueMonths[0], month2_label: revenueMonths[1], 
                            month3_label: revenueMonths[2], month4_label: revenueMonths[3], month5_label: revenueMonths[4]
                          },
                          previous_fundraises: [],
                          deck_url: '',
                          demo_link: '',
                          amount_to_raise: '',
                          terms_type: '',
                          valuation_amount: '',
                          safe_terms: '',
                          other_terms: '',
                          event_category: '',
                          investment_bankers: [],
                          funds: [],
                          angel_networks: []
                        });
                        setDealFormErrors({});
                        setIsDuplicateName(false);
                        setIsCheckingName(false);
                        setShowAddEventInDealForm(false);
                        setShowAddBankerInDealForm(false);
                        setShowAddFundInDealForm(false);
                        setShowAddNetworkInDealForm(false);
                        if (nameCheckTimeout) {
                          clearTimeout(nameCheckTimeout);
                        }
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button className="!bg-[#10B981] hover:!bg-[#059669] !text-white">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Deal
                        </Button>
                      </DialogTrigger>
                    <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">ADD NEW DEAL</DialogTitle>
                        <DialogDescription className="text-[#64748B]">
                          Fill in the startup details (same as public form) plus entity associations
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddDeal} className="space-y-6 mt-4">
                        
                        {/* Dynamic Form Fields */}
                        <DynamicDealForm
                          formData={newDeal}
                          setFormData={setNewDeal}
                          errors={dealFormErrors}
                          setErrors={setDealFormErrors}
                          isAdmin={true}
                          onBrandNameChange={handleDealNameChange}
                        />
                        {isDuplicateName && <p className="text-red-500 text-xs">A deal with this brand name already exists</p>}
                        {/* Section 8: Entity Associations (Admin Only) */}
                        <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                          <h3 className="text-md font-semibold text-brand">Entity Associations (Admin)</h3>
                          <div className="grid grid-cols-2 gap-4">
                            {/* Event */}
                            <div className="space-y-1">
                              <Label className="text-sm">Event (Optional)</Label>
                              {!showAddEventInDealForm ? (
                                <div className="flex gap-2">
                                  <Select value={newDeal.event_category} onValueChange={(v) => v === '__add_new__' ? setShowAddEventInDealForm(true) : setNewDeal({...newDeal, event_category: v})}>
                                    <SelectTrigger className="bg-white flex-1"><SelectValue placeholder="Select event" /></SelectTrigger>
                                    <SelectContent className="bg-white">
                                      {events.map((e) => <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>)}
                                      <SelectItem value="__add_new__" className="text-emerald-600 font-semibold">+ Add New</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Input value={newEventInDealForm} onChange={(e) => setNewEventInDealForm(e.target.value)} placeholder="New event name" className="bg-white flex-1" />
                                  <Button type="button" size="sm" onClick={handleAddEventInDealForm} className="!bg-emerald-500 !text-white">Add</Button>
                                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAddEventInDealForm(false)}>Cancel</Button>
                                </div>
                              )}
                            </div>
                            {/* Investment Bankers */}
                            <div className="space-y-1">
                              <Label className="text-sm">Investment Bankers (Optional)</Label>
                              {!showAddBankerInDealForm ? (
                                <div className="flex gap-2">
                                  <Select value="" onValueChange={(v) => v === '__add_new__' ? setShowAddBankerInDealForm(true) : toggleBankerSelection(v)}>
                                    <SelectTrigger className="bg-white flex-1"><SelectValue placeholder="Select bankers" /></SelectTrigger>
                                    <SelectContent className="bg-white">
                                      {investmentBankers.map((b) => <SelectItem key={b.id} value={b.name}>{(newDeal.investment_bankers || []).includes(b.name) ? '✓ ' : ''}{b.name}</SelectItem>)}
                                      <SelectItem value="__add_new__" className="text-purple-600 font-semibold">+ Add New</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Input value={newBankerInDealForm} onChange={(e) => setNewBankerInDealForm(e.target.value)} placeholder="New banker name" className="bg-white flex-1" />
                                  <Button type="button" size="sm" onClick={handleAddBankerInDealForm} className="!bg-purple-500 !text-white">Add</Button>
                                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAddBankerInDealForm(false)}>Cancel</Button>
                                </div>
                              )}
                              {(newDeal.investment_bankers || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {newDeal.investment_bankers.map((n) => <Badge key={n} className="bg-purple-100 text-purple-700 text-xs">{n} <button type="button" onClick={() => toggleBankerSelection(n)} className="ml-1">×</button></Badge>)}
                                </div>
                              )}
                            </div>
                            {/* Funds */}
                            <div className="space-y-1">
                              <Label className="text-sm">Funds (Optional)</Label>
                              {!showAddFundInDealForm ? (
                                <div className="flex gap-2">
                                  <Select value="" onValueChange={(v) => v === '__add_new__' ? setShowAddFundInDealForm(true) : toggleFundSelection(v)}>
                                    <SelectTrigger className="bg-white flex-1"><SelectValue placeholder="Select funds" /></SelectTrigger>
                                    <SelectContent className="bg-white">
                                      {funds.map((f) => <SelectItem key={f.id} value={f.name}>{(newDeal.funds || []).includes(f.name) ? '✓ ' : ''}{f.name}</SelectItem>)}
                                      <SelectItem value="__add_new__" className="text-amber-600 font-semibold">+ Add New</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Input value={newFundInDealForm} onChange={(e) => setNewFundInDealForm(e.target.value)} placeholder="New fund name" className="bg-white flex-1" />
                                  <Button type="button" size="sm" onClick={handleAddFundInDealForm} className="!bg-amber-500 !text-white">Add</Button>
                                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAddFundInDealForm(false)}>Cancel</Button>
                                </div>
                              )}
                              {(newDeal.funds || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {newDeal.funds.map((n) => <Badge key={n} className="bg-amber-100 text-amber-700 text-xs">{n} <button type="button" onClick={() => toggleFundSelection(n)} className="ml-1">×</button></Badge>)}
                                </div>
                              )}
                            </div>
                            {/* Angel Networks */}
                            <div className="space-y-1">
                              <Label className="text-sm">Angel Networks (Optional)</Label>
                              {!showAddNetworkInDealForm ? (
                                <div className="flex gap-2">
                                  <Select value="" onValueChange={(v) => v === '__add_new__' ? setShowAddNetworkInDealForm(true) : toggleNetworkSelection(v)}>
                                    <SelectTrigger className="bg-white flex-1"><SelectValue placeholder="Select networks" /></SelectTrigger>
                                    <SelectContent className="bg-white">
                                      {angelNetworks.map((n) => <SelectItem key={n.id} value={n.name}>{(newDeal.angel_networks || []).includes(n.name) ? '✓ ' : ''}{n.name}</SelectItem>)}
                                      <SelectItem value="__add_new__" className="text-pink-600 font-semibold">+ Add New</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Input value={newNetworkInDealForm} onChange={(e) => setNewNetworkInDealForm(e.target.value)} placeholder="New network name" className="bg-white flex-1" />
                                  <Button type="button" size="sm" onClick={handleAddNetworkInDealForm} className="!bg-pink-500 !text-white">Add</Button>
                                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAddNetworkInDealForm(false)}>Cancel</Button>
                                </div>
                              )}
                              {(newDeal.angel_networks || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {newDeal.angel_networks.map((n) => <Badge key={n} className="bg-pink-100 text-pink-700 text-xs">{n} <button type="button" onClick={() => toggleNetworkSelection(n)} className="ml-1">×</button></Badge>)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <Button
                          type="submit"
                          disabled={isDuplicateName || isCheckingName || uploadingDeck}
                          className="w-full h-12 font-semibold !bg-[#10B981] hover:!bg-[#059669] !text-white disabled:!bg-[#94A3B8]"
                        >
                          {isCheckingName ? 'Checking...' : uploadingDeck ? 'Uploading...' : isDuplicateName ? 'Duplicate Name' : 'Add Deal'}
                        </Button>
                      </form>
                    </DialogContent>
              </Dialog>
              )}
              </div>

              {/* Filter Row */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* Deal Type Filter */}
                <Select value={filterDealType} onValueChange={(val) => {
                  setFilterDealType(val);
                  // Reset sub-filters when type changes
                  if (val !== 'Events') setFilterEvent('all');
                }}>
                  <SelectTrigger className="w-[160px] bg-white border-[#E2E8F0]">
                    <SelectValue placeholder="Deal Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Events">Events</SelectItem>
                    <SelectItem value="Investment Banker">Investment Banker</SelectItem>
                    <SelectItem value="Fund">Fund</SelectItem>
                    <SelectItem value="Angel Network">Angel Network</SelectItem>
                  </SelectContent>
                </Select>

                {/* Event Filter - only show when type is Events */}
                {filterDealType === 'Events' && (
                  <Select value={filterEvent} onValueChange={setFilterEvent}>
                    <SelectTrigger className="w-[180px] bg-white border-[#E2E8F0]">
                      <SelectValue placeholder="Select Event" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Events</SelectItem>
                      {events.map((event) => (
                        <SelectItem key={event.id} value={event.name}>{event.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Investment Banker Filter - show when Events selected or bankers exist */}
                {(filterDealType === 'Events' || investmentBankers.length > 0) && (
                  <Select value={filterBanker} onValueChange={setFilterBanker}>
                    <SelectTrigger className="w-[180px] bg-white border-[#E2E8F0]">
                      <SelectValue placeholder="Investment Banker" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Bankers</SelectItem>
                      {investmentBankers.map((banker) => (
                        <SelectItem key={banker.id} value={banker.name}>{banker.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Fund Filter - show when Events selected or funds exist */}
                {(filterDealType === 'Events' || funds.length > 0) && (
                  <Select value={filterFund} onValueChange={setFilterFund}>
                    <SelectTrigger className="w-[160px] bg-white border-[#E2E8F0]">
                      <SelectValue placeholder="Fund" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Funds</SelectItem>
                      {funds.map((fund) => (
                        <SelectItem key={fund.id} value={fund.name}>{fund.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Angel Network Filter - show when Events selected or networks exist */}
                {(filterDealType === 'Events' || angelNetworks.length > 0) && (
                  <Select value={filterNetwork} onValueChange={setFilterNetwork}>
                    <SelectTrigger className="w-[180px] bg-white border-[#E2E8F0]">
                      <SelectValue placeholder="Angel Network" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Networks</SelectItem>
                      {angelNetworks.map((network) => (
                        <SelectItem key={network.id} value={network.name}>{network.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Clear Filters Button */}
                {(filterDealType !== 'all' || filterEvent !== 'all' || filterBanker !== 'all' || filterFund !== 'all' || filterNetwork !== 'all') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilterDealType('all');
                      setFilterEvent('all');
                      setFilterBanker('all');
                      setFilterFund('all');
                      setFilterNetwork('all');
                    }}
                    className="text-[#64748B] border-[#E2E8F0] hover:bg-[#F1F5F9]"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            {filteredDeals.length === 0 ? (
              <Card className="border-[#E2E8F0] shadow-none">
                <CardContent className="p-12 text-center">
                  <p className="text-[#64748B]">{searchDeals || filterDealType !== 'all' || filterEvent !== 'all' || filterBanker !== 'all' || filterFund !== 'all' || filterNetwork !== 'all' ? 'No deals match your filters' : 'No deals available'}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredDeals.map((deal) => (
                  <Card key={deal.id} className="border-[#E2E8F0] shadow-none">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Building2 className="h-5 w-5 text-brand" />
                            <h3 className="font-heading text-lg font-bold text-[#0F172A]">{deal.brand_name || deal.name}</h3>
                            <Badge className="bg-brand text-white">{deal.type}</Badge>
                          </div>
                          <p className="text-sm text-[#64748B] mb-2">{deal.one_liner || deal.description}</p>
                          
                          {/* All Entity Tags (Investment Bankers, Funds, Angel Networks) */}
                          {((deal.investment_bankers && deal.investment_bankers.length > 0) ||
                            (deal.funds && deal.funds.length > 0) ||
                            (deal.angel_networks && deal.angel_networks.length > 0)) && (
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              {deal.investment_bankers?.map((banker) => (
                                <span 
                                  key={`ib-${banker}`} 
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTagClasses('investment_bankers')}`}
                                >
                                  {banker}
                                </span>
                              ))}
                              {deal.funds?.map((fund) => (
                                <span 
                                  key={`fund-${fund}`} 
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTagClasses('funds')}`}
                                >
                                  {fund}
                                </span>
                              ))}
                              {deal.angel_networks?.map((network) => (
                                <span 
                                  key={`an-${network}`} 
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTagClasses('angel_networks')}`}
                                >
                                  {network}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4 text-sm mb-3">
                            {deal.amount_raising > 0 && (
                              <span className="font-semibold text-[#10B981]">{formatINR(deal.amount_raising)}</span>
                            )}
                            {deal.deck_url && (
                              <a 
                                href={getDeckUrl(deal.deck_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-brand hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FileText className="h-4 w-4" />
                                View Deck
                              </a>
                            )}
                          </div>
                          <div 
                            onClick={() => handleViewDealCommitments(deal)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#ECFDF5] border border-[#10B981] rounded-lg cursor-pointer hover:bg-[#D1FAE5] transition-colors"
                          >
                            <Users className="h-4 w-4 text-[#10B981]" />
                            <div>
                              <p className="text-xs text-[#059669] font-medium">Total Committed Capital</p>
                              <p className="text-sm font-bold text-[#10B981]">
                                {formatINR(deal.total_committed || 0)} 
                                <span className="text-xs font-normal text-[#059669] ml-1">
                                  ({deal.commitment_count || 0} angel{deal.commitment_count !== 1 ? 's' : ''})
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        {(userRole === 'admin' || userRole === 'super_admin') && (
                          <div className="flex gap-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyShareLink(deal)}
                              className="border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1] hover:text-white"
                              title="Copy Share Link"
                            >
                              <Share2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPDF(deal)}
                              className="border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
                              title="Download PDF Report"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditDeal(deal)}
                              className="border-brand text-brand hover:bg-brand hover:text-white"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteDeal(deal.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
              </TabsContent>

              {/* Approvals Sub-tab */}
              <TabsContent value="approvals">
                <Card className="border-[#E2E8F0] shadow-none">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
                      Startup Applications
                      {startupApplications.length > 0 && (
                        <Badge className="bg-amber-500 text-white">{startupApplications.length} pending</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-[#64748B]">
                      Review and approve startup applications for deals
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {startupApplications.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle className="h-8 w-8 text-[#10B981]" />
                        </div>
                        <p className="text-[#64748B] text-lg">No pending applications</p>
                        <p className="text-sm text-[#94A3B8] mt-1">All startup applications have been processed</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {startupApplications.map((app) => (
                          <Card key={app.id} className="border-[#E2E8F0] shadow-none hover:shadow-md hover:border-amber-300 transition-all cursor-pointer" onClick={() => handleViewApplicationDetails(app)}>
                            <CardContent className="p-6">
                              <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Building className="h-6 w-6 text-amber-500" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-semibold text-[#0F172A] text-lg">{app.brand_name}</h3>
                                    <Badge className="bg-purple-100 text-purple-700 text-xs">{app.stage}</Badge>
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">{app.category}</Badge>
                                  </div>
                                  <p className="text-sm text-[#64748B] mb-2">{app.one_liner}</p>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-[#64748B]">
                                    <span>Legal: {app.legal_name}</span>
                                    <span>Location: {app.location}</span>
                                    <span>Raising: ₹{app.amount_to_raise?.toLocaleString('en-IN')}</span>
                                    <span>Terms: {app.terms_type}</span>
                                  </div>
                                  <div className="mt-2 text-xs text-[#64748B]">
                                    <span className="font-medium">Founders:</span>{' '}
                                    {app.founders?.map(f => f.name).join(', ')}
                                  </div>
                                  <p className="text-xs text-brand mt-2 font-medium">Click to view full details →</p>
                                </div>
                                <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    onClick={() => handleOpenApproveDialog(app)}
                                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
                                    size="sm"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Accept
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => handleOpenRejectDialog(app)}
                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                    size="sm"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card className="border-[#E2E8F0]">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-brand" />
                  Manage Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search Bar */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search events..."
                    value={searchEvents}
                    onChange={(e) => setSearchEvents(e.target.value)}
                    className="pl-10 bg-white border-[#E2E8F0]"
                  />
                </div>

                {/* Add New Event */}
                <div className="flex gap-2 mb-6">
                  <Input
                    value={newEventName}
                    onChange={(e) => setNewEventName(e.target.value)}
                    placeholder="Enter new event name"
                    className="bg-white border-[#E2E8F0] flex-1"
                  />
                  <Button 
                    onClick={handleCreateEvent}
                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Event
                  </Button>
                </div>

                {/* Events List */}
                {filteredEvents.length === 0 ? (
                  <p className="text-[#64748B] text-center py-8">{searchEvents ? 'No events match your search' : 'No events created yet'}</p>
                ) : (
                  <div className="space-y-3">
                    {filteredEvents.map((event) => (
                      <div 
                        key={event.id} 
                        className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]"
                      >
                        {editingEvent === event.id ? (
                          <div className="flex gap-2 flex-1 mr-4">
                            <Input
                              value={editEventName}
                              onChange={(e) => setEditEventName(e.target.value)}
                              className="bg-white border-[#E2E8F0] flex-1"
                            />
                            <Button 
                              onClick={() => handleUpdateEvent(event.id)}
                              className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
                              size="sm"
                            >
                              Save
                            </Button>
                            <Button 
                              onClick={() => { setEditingEvent(null); setEditEventName(''); }}
                              variant="outline"
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div>
                              <h4 className="font-semibold text-[#0F172A]">{event.name}</h4>
                              <p className="text-xs text-[#64748B]">
                                Created: {new Date(event.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => { setEditingEvent(event.id); setEditEventName(event.name); }}
                                variant="outline"
                                size="sm"
                                className="text-brand border-brand hover:bg-brand hover:text-white"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteEvent(event.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Investment Bankers Tab */}
          <TabsContent value="bankers">
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-brand" />
                  INVESTMENT BANKERS
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search Bar */}
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search investment bankers..."
                      value={searchBankers}
                      onChange={(e) => setSearchBankers(e.target.value)}
                      className="pl-10 bg-white border-[#E2E8F0]"
                    />
                  </div>
                  <Button 
                    onClick={() => { setEditingBankerEntity(null); setShowBankerDialog(true); }}
                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Banker
                  </Button>
                </div>

                {/* Investment Bankers List */}
                {filteredBankers.length === 0 ? (
                  <p className="text-[#64748B] text-center py-8">{searchBankers ? 'No investment bankers match your search' : 'No investment bankers created yet'}</p>
                ) : (
                  <div className="space-y-3">
                    {filteredBankers.map((banker) => (
                      <div 
                        key={banker.id} 
                        className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]"
                      >
                        <div>
                          <h4 className="font-semibold text-[#0F172A]">{banker.name}</h4>
                          {banker.description && <p className="text-sm text-[#64748B]">{banker.description}</p>}
                          <div className="flex gap-3 text-xs text-[#64748B] mt-1">
                            {banker.website && <a href={banker.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Website</a>}
                            {banker.contact_email && <span>{banker.contact_email}</span>}
                            {banker.contact_phone && <span>{banker.contact_phone}</span>}
                          </div>
                          <p className="text-xs text-[#94A3B8] mt-1">
                            Created: {new Date(banker.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => { setEditingBankerEntity(banker); setShowBankerDialog(true); }}
                            variant="outline"
                            size="sm"
                            className="text-brand border-brand hover:bg-brand hover:text-white"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteBanker(banker.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dynamic Entity Form Dialog for Investment Bankers */}
            <DynamicEntityForm
              formType="investment_banker"
              entityTitle="Investment Banker"
              isOpen={showBankerDialog}
              onClose={() => { setShowBankerDialog(false); setEditingBankerEntity(null); }}
              onSave={handleSaveBankerEntity}
              editingEntity={editingBankerEntity}
            />
          </TabsContent>

          {/* Funds Tab */}
          <TabsContent value="funds">
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-brand" />
                  FUNDS
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search Bar */}
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search funds..."
                      value={searchFunds}
                      onChange={(e) => setSearchFunds(e.target.value)}
                      className="pl-10 bg-white border-[#E2E8F0]"
                    />
                  </div>
                  <Button 
                    onClick={() => { setEditingFundEntity(null); setShowFundDialog(true); }}
                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Fund
                  </Button>
                </div>

                {/* Funds List */}
                {filteredFunds.length === 0 ? (
                  <p className="text-[#64748B] text-center py-8">{searchFunds ? 'No funds match your search' : 'No funds created yet'}</p>
                ) : (
                  <div className="space-y-3">
                    {filteredFunds.map((fund) => (
                      <div 
                        key={fund.id} 
                        className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]"
                      >
                        <div>
                          <h4 className="font-semibold text-[#0F172A]">{fund.name}</h4>
                          {fund.description && <p className="text-sm text-[#64748B]">{fund.description}</p>}
                          <div className="flex gap-3 text-xs text-[#64748B] mt-1">
                            {fund.website && <a href={fund.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Website</a>}
                            {fund.contact_email && <span>{fund.contact_email}</span>}
                            {fund.contact_phone && <span>{fund.contact_phone}</span>}
                          </div>
                          <p className="text-xs text-[#94A3B8] mt-1">
                            Created: {new Date(fund.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => { setEditingFundEntity(fund); setShowFundDialog(true); }}
                            variant="outline"
                            size="sm"
                            className="text-brand border-brand hover:bg-brand hover:text-white"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteFund(fund.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dynamic Entity Form Dialog for Funds */}
            <DynamicEntityForm
              formType="fund"
              entityTitle="Fund"
              isOpen={showFundDialog}
              onClose={() => { setShowFundDialog(false); setEditingFundEntity(null); }}
              onSave={handleSaveFundEntity}
              editingEntity={editingFundEntity}
            />
          </TabsContent>

          {/* Angel Networks Tab */}
          <TabsContent value="networks">
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-brand" />
                  ANGEL NETWORKS
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search Bar */}
                <div className="flex gap-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search angel networks..."
                      value={searchNetworks}
                      onChange={(e) => setSearchNetworks(e.target.value)}
                      className="pl-10 bg-white border-[#E2E8F0]"
                    />
                  </div>
                  <Button 
                    onClick={() => { setEditingNetworkEntity(null); setShowNetworkDialog(true); }}
                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Network
                  </Button>
                </div>

                {/* Angel Networks List */}
                {filteredNetworks.length === 0 ? (
                  <p className="text-[#64748B] text-center py-8">{searchNetworks ? 'No angel networks match your search' : 'No angel networks created yet'}</p>
                ) : (
                  <div className="space-y-3">
                    {filteredNetworks.map((network) => (
                      <div 
                        key={network.id} 
                        className="flex items-center justify-between p-4 bg-[#F8F9FA] rounded-lg border border-[#E2E8F0]"
                      >
                        <div>
                          <h4 className="font-semibold text-[#0F172A]">{network.name}</h4>
                          {network.description && <p className="text-sm text-[#64748B]">{network.description}</p>}
                          <div className="flex gap-3 text-xs text-[#64748B] mt-1">
                            {network.website && <a href={network.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Website</a>}
                            {network.contact_email && <span>{network.contact_email}</span>}
                            {network.contact_phone && <span>{network.contact_phone}</span>}
                          </div>
                          <p className="text-xs text-[#94A3B8] mt-1">
                            Created: {new Date(network.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => { setEditingNetworkEntity(network); setShowNetworkDialog(true); }}
                            variant="outline"
                            size="sm"
                            className="text-brand border-brand hover:bg-brand hover:text-white"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteNetwork(network.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dynamic Entity Form Dialog for Angel Networks */}
            <DynamicEntityForm
              formType="angel_network"
              entityTitle="Angel Network"
              isOpen={showNetworkDialog}
              onClose={() => { setShowNetworkDialog(false); setEditingNetworkEntity(null); }}
              onSave={handleSaveNetworkEntity}
              editingEntity={editingNetworkEntity}
            />
          </TabsContent>

          {/* Users Tab with Sub-tabs */}
          <TabsContent value="users">
            {/* Search Bar, Role Filter, and Invite Button */}
            <div className="mb-4 flex gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search angels by name or email..."
                  value={searchUsers}
                  onChange={(e) => setSearchUsers(e.target.value)}
                  className="pl-10 bg-white border-[#E2E8F0]"
                />
              </div>
              {/* Role Filter Dropdown */}
              <Select value={filterUserRole} onValueChange={setFilterUserRole}>
                <SelectTrigger className="w-40 bg-white border-[#E2E8F0]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="investor">Angels</SelectItem>
                  <SelectItem value="team">Team</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                  {userRole === 'super_admin' && (
                    <SelectItem value="super_admin">Super Admins</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Dialog onOpenChange={(open) => {
                if (!open) {
                  // Clear form when dialog closes
                  setInviteData({ first_name: '', last_name: '', email: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="!bg-brand hover:!bg-brand-hover !text-white">
                    <Plus className="mr-2 h-4 w-4" />
                    Invite Angel
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">INVITE ANGEL</DialogTitle>
                    <DialogDescription className="text-[#64748B]">
                      Send an invitation to join the network
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleInvite} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-[#0F172A] font-medium">First Name</Label>
                      <Input
                        value={inviteData.first_name}
                        onChange={(e) => setInviteData({...inviteData, first_name: e.target.value})}
                        className="border-[#E2E8F0]"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0F172A] font-medium">Last Name</Label>
                      <Input
                        value={inviteData.last_name}
                        onChange={(e) => setInviteData({...inviteData, last_name: e.target.value})}
                        className="border-[#E2E8F0]"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#0F172A] font-medium">Email</Label>
                      <Input
                        type="email"
                        value={inviteData.email}
                        onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                        className="border-[#E2E8F0]"
                        placeholder="user@example.com"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full !bg-[#10B981] hover:!bg-[#059669] !text-white h-12 font-semibold"
                    >
                      Send Invitation
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Sub-tabs for Active and Pending Approval */}
            <Tabs value={usersSubTab} onValueChange={setUsersSubTab}>
              <TabsList className="bg-white border border-[#E2E8F0] mb-6">
                <TabsTrigger 
                  value="active" 
                  className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white font-semibold"
                >
                  Active ({filteredApprovedUsers.length})
                </TabsTrigger>
                <TabsTrigger 
                  value="pending" 
                  className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-white font-semibold"
                >
                  Pending Approval ({filteredPendingMembers.length})
                </TabsTrigger>
              </TabsList>

              {/* Active Users Sub-tab */}
              <TabsContent value="active">
                {filteredApprovedUsers.length === 0 ? (
                  <Card className="border-[#E2E8F0] shadow-none">
                    <CardContent className="p-12 text-center">
                      <p className="text-[#64748B]">{searchUsers ? 'No angels match your search' : 'No active angels'}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {filteredApprovedUsers.map((user) => (
                      <Card 
                        key={user.email} 
                        className="border-[#E2E8F0] shadow-none hover:shadow-md hover:border-brand transition-all"
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-3">
                            <div 
                              className="w-10 h-10 bg-[#ECFDF5] rounded-md flex items-center justify-center flex-shrink-0 cursor-pointer"
                              onClick={() => handleViewUserCommitments(user)}
                            >
                              <UserCheck className="h-5 w-5 text-[#10B981]" />
                            </div>
                            <div className="flex-1">
                              <div className="cursor-pointer" onClick={() => handleViewUserCommitments(user)}>
                                <h3 className="font-semibold text-[#0F172A] mb-1">
                                  {user.first_name} {user.last_name}
                                </h3>
                                <p className="text-sm text-[#64748B]">{user.email}</p>
                                <p className="text-sm text-[#64748B]">Phone: {user.phone}</p>
                                {user.capital_deployment && (
                                  <p className="text-xs text-[#64748B] mt-1">
                                    Capital: ₹{user.capital_deployment.toLocaleString('en-IN')}
                                  </p>
                                )}
                                <p className="text-xs text-brand mt-2 font-medium">Click to view commitments →</p>
                              </div>
                              
                              {/* Display User Associations */}
                              {((user.investment_bankers && user.investment_bankers.length > 0) ||
                                (user.funds && user.funds.length > 0) ||
                                (user.angel_networks && user.angel_networks.length > 0)) && (
                                <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                                  <p className="text-xs text-[#64748B] font-medium mb-2">Associated With:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {user.investment_bankers?.map((banker) => (
                                      <Badge 
                                        key={banker} 
                                        className={`${getTagClasses('investment_bankers')} text-xs font-medium`}
                                      >
                                        {banker}
                                      </Badge>
                                    ))}
                                    {user.funds?.map((fund) => (
                                      <Badge key={fund} className={`${getTagClasses('funds')} text-xs font-medium`}>
                                        {fund}
                                      </Badge>
                                    ))}
                                    {user.angel_networks?.map((network) => (
                                      <Badge key={network} className={`${getTagClasses('angel_networks')} text-xs font-medium`}>
                                        {network}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className={
                                user.role === 'super_admin' ? 'bg-purple-600 text-white' :
                                user.role === 'admin' ? 'bg-brand text-white' :
                                user.role === 'team' ? 'bg-[#F59E0B] text-white' :
                                'bg-[#10B981] text-white'
                              }>
                                {user.role === 'super_admin' ? 'Super Admin' : 
                                 user.role === 'admin' ? 'Admin' :
                                 user.role === 'team' ? 'Team' : 'Angel'}
                              </Badge>
                              
                              {/* Display Access Tags */}
                              <div className="mt-2">
                                <AccessTagsBadges accessTags={user.access_tags} />
                              </div>
                              
                              {/* Edit Access Tags Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openAccessTagsForEdit(user)}
                                className="text-xs h-8 px-2 border-brand text-brand hover:bg-brand hover:text-white"
                              >
                                <Shield className="h-3 w-3 mr-1" />
                                Access Tags
                              </Button>

                              {/* Edit Profile Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenEditProfile(user)}
                                className="text-xs h-8 px-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                Edit Profile
                              </Button>

                              {/* Edit Associations Button */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenUserAssociations(user)}
                                className="text-xs h-8 px-2 border-[#E2E8F0] hover:border-brand hover:text-brand"
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                Associations
                              </Button>
                              
                              {/* Role dropdown - only show if current user can change this user's role */}
                              {(userRole === 'super_admin' || 
                                (userRole === 'admin' && user.role !== 'super_admin')) && (
                                <Select
                                  value={user.role}
                                  onValueChange={(newRole) => handleRoleChange(user.email, newRole)}
                                >
                                  <SelectTrigger className="w-32 h-8 text-xs">
                                    <SelectValue placeholder="Change role" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="investor">Angel</SelectItem>
                                    <SelectItem value="team">Team</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    {userRole === 'super_admin' && (
                                      <SelectItem value="super_admin">Super Admin</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Pending Approval Sub-tab */}
              <TabsContent value="pending">
                {filteredPendingMembers.length === 0 ? (
                  <Card className="border-[#E2E8F0] shadow-none">
                    <CardContent className="p-12 text-center">
                      <p className="text-[#64748B]">{searchUsers ? 'No pending angels match your search' : 'No pending approvals'}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {filteredPendingMembers.map((member) => (
                      <Card key={member.email} className="border-[#E2E8F0] shadow-none">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-3">
                                <Clock className="h-5 w-5 text-[#F59E0B]" />
                                <h3 className="font-heading text-lg font-bold text-[#0F172A]">
                                  {member.first_name} {member.last_name}
                                </h3>
                                {member.invited_deal_id && (
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    Via Shared Link
                                  </Badge>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                                <div>
                                  <p className="text-xs text-[#64748B] font-medium">Email</p>
                                  <p className="text-sm text-[#0F172A]">{member.email}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#64748B] font-medium">Phone</p>
                                  <p className="text-sm text-[#0F172A]">{member.phone}</p>
                                </div>
                                {member.invited_deal_id && (
                                  <div>
                                    <p className="text-xs text-[#64748B] font-medium">Signed Up From Deal</p>
                                    <p className="text-sm text-brand font-medium">
                                      {deals.find(d => d.id === member.invited_deal_id)?.name || member.invited_deal_id}
                                    </p>
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs text-[#64748B] font-medium">Registered</p>
                                  <p className="text-sm text-[#0F172A]">
                                    {new Date(member.created_at).toLocaleDateString('en-IN', { 
                                      year: 'numeric', 
                                      month: 'long', 
                                      day: 'numeric' 
                                    })}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-2">
                              <Button
                                onClick={() => handleApprove(member.email)}
                                className="!bg-[#10B981] hover:!bg-[#059669] !text-white whitespace-nowrap"
                              >
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleReject(member.email)}
                                className="!bg-[#EF4444] hover:!bg-[#DC2626] !text-white whitespace-nowrap"
                              >
                                Decline
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Forms Tab */}
          <TabsContent value="forms">
            <Card className="border-[#E2E8F0] shadow-none">
              <CardHeader>
                <CardTitle className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
                  <Settings2 className="h-5 w-5 text-brand" />
                  FORM BUILDER
                </CardTitle>
                <CardDescription className="text-[#64748B]">
                  Configure form fields for different entity types. Changes will reflect across all forms.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="startup" className="w-full">
                  <TabsList className="bg-white border border-[#E2E8F0] mb-6">
                    <TabsTrigger 
                      value="startup" 
                      className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
                    >
                      Startup
                    </TabsTrigger>
                    <TabsTrigger 
                      value="investment_banker"
                      className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
                    >
                      Investment Banker
                    </TabsTrigger>
                    <TabsTrigger 
                      value="angel_network"
                      className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
                    >
                      Angel Network
                    </TabsTrigger>
                    <TabsTrigger 
                      value="fund"
                      className="data-[state=active]:bg-brand data-[state=active]:text-white font-semibold"
                    >
                      Fund
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="startup">
                    <FormBuilder formType="startup" formTitle="Startup" />
                  </TabsContent>
                  
                  <TabsContent value="investment_banker">
                    <FormBuilder formType="investment_banker" formTitle="Investment Banker" />
                  </TabsContent>
                  
                  <TabsContent value="angel_network">
                    <FormBuilder formType="angel_network" formTitle="Angel Network" />
                  </TabsContent>
                  
                  <TabsContent value="fund">
                    <FormBuilder formType="fund" formTitle="Fund" />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Deal Commitments Dialog */}
        <Dialog open={showCommitmentsDialog} onOpenChange={setShowCommitmentsDialog}>
          <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader className="pr-8">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
                    COMMITMENTS FOR {selectedDeal?.brand_name || selectedDeal?.name}
                  </DialogTitle>
                  <DialogDescription className="text-[#64748B]">
                    Manage angel commitments for this deal
                  </DialogDescription>
                </div>
                {(userRole === 'admin' || userRole === 'super_admin') && (
                  <Button
                    onClick={() => setShowAddInvestorDialog(true)}
                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white flex-shrink-0"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Angel
                  </Button>
                )}
              </div>
            </DialogHeader>

            {dealCommitments.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
                <p className="text-[#64748B]">No commitments yet for this deal</p>
                {(userRole === 'admin' || userRole === 'super_admin') && (
                  <Button
                    onClick={() => setShowAddInvestorDialog(true)}
                    variant="outline"
                    className="mt-4 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Angel
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F1F5F9]">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-[#0F172A]">Angel Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-[#0F172A]">Email</th>
                      <th className="text-left p-3 text-sm font-semibold text-[#0F172A]">Phone</th>
                      <th className="text-right p-3 text-sm font-semibold text-[#0F172A]">Amount Committed</th>
                      <th className="text-left p-3 text-sm font-semibold text-[#0F172A]">Date</th>
                      {(userRole === 'admin' || userRole === 'super_admin') && (
                        <th className="text-right p-3 text-sm font-semibold text-[#0F172A]">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {dealCommitments.map((commitment, index) => (
                      <tr key={commitment.id || index} className="border-b border-[#E2E8F0] hover:bg-[#F8F9FA]">
                        <td className="p-3 text-sm text-[#0F172A] font-medium">{commitment.user_name}</td>
                        <td className="p-3 text-sm text-[#64748B]">{commitment.user_email}</td>
                        <td className="p-3 text-sm text-[#64748B]">{commitment.user_phone || '-'}</td>
                        <td className="p-3 text-sm font-bold text-[#10B981] text-right">{formatINR(commitment.amount_inr)}</td>
                        <td className="p-3 text-sm text-[#64748B]">
                          {new Date(commitment.committed_at).toLocaleDateString('en-IN')}
                        </td>
                        {(userRole === 'admin' || userRole === 'super_admin') && (
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditCommitment(commitment, true)}
                                className="h-8 px-2 text-brand border-brand hover:bg-brand hover:text-white"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteCommitment(commitment)}
                                className="h-8 px-2 text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F8F9FA]">
                    <tr>
                      <td colSpan="3" className="p-3 text-sm font-semibold text-[#0F172A]">Total</td>
                      <td className="p-3 text-sm font-bold text-[#10B981] text-right">
                        {formatINR(dealCommitments.reduce((sum, c) => sum + c.amount_inr, 0))}
                      </td>
                      <td colSpan={(userRole === 'admin' || userRole === 'super_admin') ? 2 : 1}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Commitments Dialog */}
        <Dialog open={showUserCommitmentsDialog} onOpenChange={setShowUserCommitmentsDialog}>
          <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader className="pr-8">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
                    COMMITMENTS BY {selectedUser?.first_name} {selectedUser?.last_name}
                  </DialogTitle>
                  <DialogDescription className="text-[#64748B]">
                    Manage investments made by this angel
                  </DialogDescription>
                </div>
                {(userRole === 'admin' || userRole === 'super_admin') && (
                  <Button
                    onClick={() => setShowAddDealDialog(true)}
                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white flex-shrink-0"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Investment
                  </Button>
                )}
              </div>
            </DialogHeader>
            
            {userCommitments.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
                <p className="text-[#64748B]">No commitments made yet</p>
                {(userRole === 'admin' || userRole === 'super_admin') && (
                  <Button
                    onClick={() => setShowAddDealDialog(true)}
                    variant="outline"
                    className="mt-4 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Investment
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F1F5F9]">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-[#0F172A]">Deal Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-[#0F172A]">Deal Type</th>
                      <th className="text-right p-3 text-sm font-semibold text-[#0F172A]">Amount Committed</th>
                      <th className="text-left p-3 text-sm font-semibold text-[#0F172A]">Date</th>
                      {(userRole === 'admin' || userRole === 'super_admin') && (
                        <th className="text-right p-3 text-sm font-semibold text-[#0F172A]">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {userCommitments.map((commitment, index) => (
                      <tr key={commitment.id || index} className="border-b border-[#E2E8F0] hover:bg-[#F8F9FA]">
                        <td className="p-3 text-sm text-[#0F172A] font-medium">{commitment.deal_name}</td>
                        <td className="p-3 text-sm text-[#64748B]">
                          <Badge className="bg-brand text-white">{commitment.deal_type}</Badge>
                        </td>
                        <td className="p-3 text-sm font-bold text-[#10B981] text-right">{formatINR(commitment.amount_inr)}</td>
                        <td className="p-3 text-sm text-[#64748B]">
                          {new Date(commitment.committed_at).toLocaleDateString('en-IN')}
                        </td>
                        {(userRole === 'admin' || userRole === 'super_admin') && (
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditCommitment(commitment, false)}
                                className="h-8 px-2 text-brand border-brand hover:bg-brand hover:text-white"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteCommitment(commitment)}
                                className="h-8 px-2 text-red-500 border-red-500 hover:bg-red-500 hover:text-white"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F8F9FA]">
                    <tr>
                      <td colSpan="2" className="p-3 text-sm font-semibold text-[#0F172A]">Total</td>
                      <td className="p-3 text-sm font-bold text-[#10B981] text-right">
                        {formatINR(userCommitments.reduce((sum, c) => sum + c.amount_inr, 0))}
                      </td>
                      <td colSpan={(userRole === 'admin' || userRole === 'super_admin') ? 2 : 1}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Deal Dialog */}
        <Dialog open={showEditDealDialog} onOpenChange={(open) => {
          if (!open) {
            setEditingDeal(null);
            setEditFormErrors({});
          }
          setShowEditDealDialog(open);
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">EDIT DEAL</DialogTitle>
              <DialogDescription className="text-[#64748B]">
                Update the startup details
              </DialogDescription>
            </DialogHeader>
            {editingDeal && (
              <div className="space-y-6 mt-4">
                
                {/* Dynamic Form Fields */}
                <DynamicDealForm
                  formData={editingDeal}
                  setFormData={setEditingDeal}
                  errors={editFormErrors}
                  setErrors={setEditFormErrors}
                  isAdmin={true}
                />
                {/* Section 8: Entity Associations */}
                <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-brand">Entity Associations (Admin)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Event */}
                    <div className="space-y-1">
                      <Label className="text-sm">Event (Optional)</Label>
                      <Select value={editingDeal.event_category || ''} onValueChange={(v) => setEditingDeal({...editingDeal, event_category: v})}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select event" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {events.map((e) => <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Investment Bankers */}
                    <div className="space-y-1">
                      <Label className="text-sm">Investment Bankers (Optional)</Label>
                      <Select value="" onValueChange={(v) => toggleEditBankerSelection(v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select bankers" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {investmentBankers.map((b) => <SelectItem key={b.id} value={b.name}>{(editingDeal.investment_bankers || []).includes(b.name) ? '✓ ' : ''}{b.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(editingDeal.investment_bankers || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {editingDeal.investment_bankers.map((n) => <Badge key={n} className="bg-purple-100 text-purple-700 text-xs">{n} <button type="button" onClick={() => toggleEditBankerSelection(n)} className="ml-1">×</button></Badge>)}
                        </div>
                      )}
                    </div>
                    {/* Funds */}
                    <div className="space-y-1">
                      <Label className="text-sm">Funds (Optional)</Label>
                      <Select value="" onValueChange={(v) => toggleEditFundSelection(v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select funds" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {funds.map((f) => <SelectItem key={f.id} value={f.name}>{(editingDeal.funds || []).includes(f.name) ? '✓ ' : ''}{f.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(editingDeal.funds || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {editingDeal.funds.map((n) => <Badge key={n} className="bg-amber-100 text-amber-700 text-xs">{n} <button type="button" onClick={() => toggleEditFundSelection(n)} className="ml-1">×</button></Badge>)}
                        </div>
                      )}
                    </div>
                    {/* Angel Networks */}
                    <div className="space-y-1">
                      <Label className="text-sm">Angel Networks (Optional)</Label>
                      <Select value="" onValueChange={(v) => toggleEditNetworkSelection(v)}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select networks" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {angelNetworks.map((n) => <SelectItem key={n.id} value={n.name}>{(editingDeal.angel_networks || []).includes(n.name) ? '✓ ' : ''}{n.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(editingDeal.angel_networks || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {editingDeal.angel_networks.map((n) => <Badge key={n} className="bg-pink-100 text-pink-700 text-xs">{n} <button type="button" onClick={() => toggleEditNetworkSelection(n)} className="ml-1">×</button></Badge>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 9: Deal-Specific Custom Fields */}
                <div className="bg-amber-50 p-4 rounded-lg">
                  <DealCustomFields
                    customFields={editingDeal.custom_fields || []}
                    onChange={(fields) => setEditingDeal({...editingDeal, custom_fields: fields})}
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowEditDealDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateDeal} disabled={uploadingEditDeck} className="!bg-[#10B981] hover:!bg-[#059669] !text-white">
                    {uploadingEditDeck ? 'Uploading...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* User Associations Dialog */}
        <Dialog open={showUserAssociationsDialog} onOpenChange={setShowUserAssociationsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
                Edit Angel Associations
              </DialogTitle>
              <DialogDescription className="text-[#64748B]">
                {editingUserAssociations && (
                  <>Associate <strong>{editingUserAssociations.first_name} {editingUserAssociations.last_name}</strong> with Investment Bankers, Funds, and Angel Networks</>
                )}
              </DialogDescription>
            </DialogHeader>
            {editingUserAssociations && (
              <div className="space-y-4 mt-4">
                {/* Investment Bankers Selection */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Investment Bankers</Label>
                  <Select
                    value=""
                    onValueChange={(value) => toggleUserBankerSelection(value)}
                  >
                    <SelectTrigger className="bg-white border-[#E2E8F0]">
                      <SelectValue placeholder="Select investment bankers..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {investmentBankers.map((banker) => (
                        <SelectItem key={banker.id} value={banker.name}>
                          <div className="flex items-center gap-2">
                            {(userAssociationsData.investment_bankers || []).includes(banker.name) && (
                              <span className="text-[#10B981]">✓</span>
                            )}
                            {banker.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(userAssociationsData.investment_bankers || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(userAssociationsData.investment_bankers || []).map((name) => (
                        <Badge 
                          key={name} 
                          className={`${getTagClasses('investment_bankers')} text-xs font-medium`}
                        >
                          {name}
                          <button 
                            type="button" 
                            onClick={() => toggleUserBankerSelection(name)} 
                            className="ml-1 hover:opacity-70"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Funds Selection */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Funds</Label>
                  <Select
                    value=""
                    onValueChange={(value) => toggleUserFundSelection(value)}
                  >
                    <SelectTrigger className="bg-white border-[#E2E8F0]">
                      <SelectValue placeholder="Select funds..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {funds.map((fund) => (
                        <SelectItem key={fund.id} value={fund.name}>
                          <div className="flex items-center gap-2">
                            {(userAssociationsData.funds || []).includes(fund.name) && (
                              <span className="text-[#10B981]">✓</span>
                            )}
                            {fund.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(userAssociationsData.funds || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(userAssociationsData.funds || []).map((name) => (
                        <Badge key={name} className={`${getTagClasses('funds')} text-xs font-medium`}>
                          {name}
                          <button 
                            type="button" 
                            onClick={() => toggleUserFundSelection(name)} 
                            className="ml-1 hover:opacity-70"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Angel Networks Selection */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Angel Networks</Label>
                  <Select
                    value=""
                    onValueChange={(value) => toggleUserNetworkSelection(value)}
                  >
                    <SelectTrigger className="bg-white border-[#E2E8F0]">
                      <SelectValue placeholder="Select angel networks..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {angelNetworks.map((network) => (
                        <SelectItem key={network.id} value={network.name}>
                          <div className="flex items-center gap-2">
                            {(userAssociationsData.angel_networks || []).includes(network.name) && (
                              <span className="text-[#10B981]">✓</span>
                            )}
                            {network.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(userAssociationsData.angel_networks || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(userAssociationsData.angel_networks || []).map((name) => (
                        <Badge key={name} className={`${getTagClasses('angel_networks')} text-xs font-medium`}>
                          {name}
                          <button 
                            type="button" 
                            onClick={() => toggleUserNetworkSelection(name)} 
                            className="ml-1 hover:opacity-70"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowUserAssociationsDialog(false);
                      setEditingUserAssociations(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpdateUserAssociations} 
                    className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
                  >
                    Save Associations
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Profile Dialog */}
        <Dialog open={showEditProfileDialog} onOpenChange={(open) => {
          if (!open) {
            setEditingProfileUser(null);
          }
          setShowEditProfileDialog(open);
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
                EDIT ANGEL PROFILE
              </DialogTitle>
              <DialogDescription className="text-[#64748B]">
                {editingProfileUser && (
                  <>Update profile details for <strong>{editingProfileUser.first_name} {editingProfileUser.last_name}</strong></>
                )}
              </DialogDescription>
            </DialogHeader>
            {editingProfileUser && (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">First Name</Label>
                  <Input
                    value={editProfileData.first_name}
                    onChange={(e) => setEditProfileData({...editProfileData, first_name: e.target.value})}
                    className="border-[#E2E8F0]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Last Name</Label>
                  <Input
                    value={editProfileData.last_name}
                    onChange={(e) => setEditProfileData({...editProfileData, last_name: e.target.value})}
                    className="border-[#E2E8F0]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Phone</Label>
                  <Input
                    value={editProfileData.phone}
                    onChange={(e) => setEditProfileData({...editProfileData, phone: formatPhoneInput(e.target.value)})}
                    className="border-[#E2E8F0]"
                    placeholder="+91XXXXXXXXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Capital Deployment (INR)</Label>
                  <Input
                    value={editProfileData.capital_deployment}
                    onChange={(e) => setEditProfileData({...editProfileData, capital_deployment: formatNumberInput(e.target.value)})}
                    className="border-[#E2E8F0]"
                    placeholder="e.g. 1000000"
                  />
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowEditProfileDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveProfile} className="!bg-[#10B981] hover:!bg-[#059669] !text-white">
                    Save Profile
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Access Tags Selector Dialog */}
        <AccessTagsSelector
          isOpen={showAccessTagsDialog}
          onClose={() => { setShowAccessTagsDialog(false); setAccessTagsUser(null); }}
          user={accessTagsUser}
          events={events.map(e => e.name)}
          funds={funds}
          angelNetworks={angelNetworks}
          investmentBankers={investmentBankers}
          onSave={handleSaveAccessTags}
          mode={accessTagsMode}
        />

        {/* Approve Application Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
                Approve Application
              </DialogTitle>
              <DialogDescription className="text-[#64748B]">
                {selectedApplication && (
                  <>Select where to list <strong>{selectedApplication.brand_name}</strong></>
                )}
              </DialogDescription>
            </DialogHeader>
            {selectedApplication && (
              <div className="space-y-4 mt-4">
                {/* Event Selection */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Event (Optional)</Label>
                  {!showAddEventInApproval ? (
                    <div className="flex gap-2">
                      <Select
                        value={approvalData.event_category}
                        onValueChange={(value) => setApprovalData({...approvalData, event_category: value})}
                      >
                        <SelectTrigger className="bg-white border-[#E2E8F0] flex-1">
                          <SelectValue placeholder="Select event..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {events.map((event) => (
                            <SelectItem key={event.id} value={event.name}>{event.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddEventInApproval(true); setNewEntityName(''); }} className="text-emerald-600 border-emerald-300 hover:bg-emerald-50">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={newEntityName} onChange={(e) => setNewEntityName(e.target.value)} placeholder="New event name" className="flex-1" />
                      <Button type="button" size="sm" onClick={handleAddNewEventInApproval} className="!bg-emerald-500 !text-white">Add</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowAddEventInApproval(false)}>Cancel</Button>
                    </div>
                  )}
                </div>

                {/* Investment Bankers Selection */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Investment Bankers (Optional)</Label>
                  {!showAddBankerInApproval ? (
                    <div className="flex gap-2">
                      <Select value="" onValueChange={toggleApprovalBanker}>
                        <SelectTrigger className="bg-white border-[#E2E8F0] flex-1">
                          <SelectValue placeholder="Select investment bankers..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {investmentBankers.map((banker) => (
                            <SelectItem key={banker.id} value={banker.name}>
                              <div className="flex items-center gap-2">
                                {(approvalData.investment_bankers || []).includes(banker.name) && <span className="text-purple-600">✓</span>}
                                {banker.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddBankerInApproval(true); setNewEntityName(''); }} className="text-purple-600 border-purple-300 hover:bg-purple-50">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={newEntityName} onChange={(e) => setNewEntityName(e.target.value)} placeholder="New banker name" className="flex-1" />
                      <Button type="button" size="sm" onClick={handleAddNewBankerInApproval} className="!bg-purple-500 !text-white">Add</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowAddBankerInApproval(false)}>Cancel</Button>
                    </div>
                  )}
                  {(approvalData.investment_bankers || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(approvalData.investment_bankers || []).map((name) => (
                        <Badge key={name} className={`${getTagClasses('investment_bankers')} text-xs`}>
                          {name}
                          <button type="button" onClick={() => toggleApprovalBanker(name)} className="ml-1 hover:opacity-70">×</button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Funds Selection */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Funds (Optional)</Label>
                  {!showAddFundInApproval ? (
                    <div className="flex gap-2">
                      <Select value="" onValueChange={toggleApprovalFund}>
                        <SelectTrigger className="bg-white border-[#E2E8F0] flex-1">
                          <SelectValue placeholder="Select funds..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {funds.map((fund) => (
                            <SelectItem key={fund.id} value={fund.name}>
                              <div className="flex items-center gap-2">
                                {(approvalData.funds || []).includes(fund.name) && <span className="text-amber-600">✓</span>}
                                {fund.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddFundInApproval(true); setNewEntityName(''); }} className="text-amber-600 border-amber-300 hover:bg-amber-50">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={newEntityName} onChange={(e) => setNewEntityName(e.target.value)} placeholder="New fund name" className="flex-1" />
                      <Button type="button" size="sm" onClick={handleAddNewFundInApproval} className="!bg-amber-500 !text-white">Add</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowAddFundInApproval(false)}>Cancel</Button>
                    </div>
                  )}
                  {(approvalData.funds || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(approvalData.funds || []).map((name) => (
                        <Badge key={name} className={`${getTagClasses('funds')} text-xs`}>
                          {name}
                          <button type="button" onClick={() => toggleApprovalFund(name)} className="ml-1 hover:opacity-70">×</button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Angel Networks Selection */}
                <div className="space-y-2">
                  <Label className="text-[#0F172A] font-medium">Angel Networks (Optional)</Label>
                  {!showAddNetworkInApproval ? (
                    <div className="flex gap-2">
                      <Select value="" onValueChange={toggleApprovalNetwork}>
                        <SelectTrigger className="bg-white border-[#E2E8F0] flex-1">
                          <SelectValue placeholder="Select angel networks..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {angelNetworks.map((network) => (
                            <SelectItem key={network.id} value={network.name}>
                              <div className="flex items-center gap-2">
                                {(approvalData.angel_networks || []).includes(network.name) && <span className="text-pink-600">✓</span>}
                                {network.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="sm" onClick={() => { setShowAddNetworkInApproval(true); setNewEntityName(''); }} className="text-pink-600 border-pink-300 hover:bg-pink-50">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input value={newEntityName} onChange={(e) => setNewEntityName(e.target.value)} placeholder="New network name" className="flex-1" />
                      <Button type="button" size="sm" onClick={handleAddNewNetworkInApproval} className="!bg-pink-500 !text-white">Add</Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowAddNetworkInApproval(false)}>Cancel</Button>
                    </div>
                  )}
                  {(approvalData.angel_networks || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(approvalData.angel_networks || []).map((name) => (
                        <Badge key={name} className={`${getTagClasses('angel_networks')} text-xs`}>
                          {name}
                          <button type="button" onClick={() => toggleApprovalNetwork(name)} className="ml-1 hover:opacity-70">×</button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
                  <Button onClick={handleApproveApplication} className="!bg-[#10B981] hover:!bg-[#059669] !text-white">
                    Approve & Create Deal
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Application Details Dialog - New Redesigned Component */}
        <ApplicationDetailsDialog
          open={showApplicationDetailsDialog}
          onOpenChange={setShowApplicationDetailsDialog}
          application={selectedApplication}
          onApprove={handleOpenApproveDialog}
          onReject={handleOpenRejectDialog}
        />

        {/* Reject Application Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
                Reject Application
              </DialogTitle>
              <DialogDescription className="text-[#64748B]">
                {selectedApplication && (
                  <>Please provide a reason for rejecting <strong>{selectedApplication.brand_name}</strong></>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[#0F172A] font-medium">Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please explain why this application is being rejected..."
                  className="border-[#E2E8F0] min-h-[100px]"
                />
              </div>
              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
                <Button 
                  onClick={handleRejectApplication} 
                  className="!bg-red-500 hover:!bg-red-600 !text-white"
                  disabled={!rejectionReason.trim()}
                >
                  Reject Application
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Investor to Deal Dialog */}
        <AddInvestorToDeal
          isOpen={showAddInvestorDialog}
          onClose={() => setShowAddInvestorDialog(false)}
          deal={selectedDeal}
          investors={allUsers.filter(u => u.status === 'approved')}
          existingCommitments={dealCommitments}
          onSuccess={handleRefreshDealCommitments}
        />

        {/* Add Deal to Investor Dialog */}
        <AddDealToInvestor
          isOpen={showAddDealDialog}
          onClose={() => setShowAddDealDialog(false)}
          user={selectedUser}
          deals={deals}
          existingCommitments={userCommitments}
          onSuccess={handleRefreshUserCommitments}
        />

        {/* Edit Commitment Dialog */}
        <EditCommitmentDialog
          isOpen={showEditCommitmentDialog}
          onClose={() => {
            setShowEditCommitmentDialog(false);
            setEditingCommitment(null);
          }}
          commitment={editingCommitment}
          isAdmin={userRole === 'admin' || userRole === 'super_admin'}
          onSuccess={() => {
            if (editingCommitment?.fromDealView) {
              handleRefreshDealCommitments();
            } else {
              handleRefreshUserCommitments();
            }
          }}
        />
      </div>
    </div>
  );
}
